/**
 * GrowthPilot Component - Sistema de Cache e Navegação Otimizada
 * 
 * OTIMIZAÇÕES IMPLEMENTADAS:
 * 
 * 1. SISTEMA DE CACHE:
 *    - Cache com chave `${year}-${month}` para cada mês
 *    - Verificação de cache antes de fazer requisição à API
 *    - Armazenamento automático de dados após carregamento bem-sucedido
 *    - Invalidação de cache quando posts são editados
 *    - Limpeza automática do cache no logout
 * 
 * 2. ESTADOS DE LOADING SEPARADOS:
 *    - `isLoading`: Apenas para carregamento inicial da tela
 *    - `isNavigating`: Para navegação entre meses
 *    - `showCacheIndicator`: Para mostrar quando dados vêm do cache
 * 
 * 3. EXPERIÊNCIA DO USUÁRIO:
 *    - Spinner sutil durante navegação (não bloqueia interface)
 *    - Indicador visual de cache ativo (⚡ Cache)
 *    - Calendário permanece visível durante navegação
 *    - Transições suaves entre meses
 * 
 * 4. PERFORMANCE:
 *    - Evita requisições desnecessárias à API
 *    - Carregamento instantâneo de meses já visitados
 *    - Redução significativa de tempo de navegação
 *    - Menor consumo de dados e recursos do servidor
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import translations from '../utils/translations.json';
import { ChakraProvider, useMediaQuery, Modal, ModalOverlay, ModalContent, ModalBody, ModalFooter, Button, Stack,  ModalHeader, ModalCloseButton, RadioGroup, Radio } from "@chakra-ui/react";
import { MdMenu, MdDashboard, MdSettings, MdLocalActivity, MdAutorenew,  MdArrowBack, MdFace, MdEventAvailable, MdForum, MdEdit, MdRemoveRedEye,  MdFileUpload, MdSend, MdKeyboardArrowLeft, MdDownload, MdSave } from "react-icons/md";
import { FaInstagram, FaRegTimesCircle, FaRobot, FaCheckCircle, FaPlus } from "react-icons/fa";
import { RiCopperCoinLine } from "react-icons/ri";
import Swal from 'sweetalert2';
import './GrowthPilot.css';
import axios from 'axios';
import { SketchPicker } from 'react-color';
import { colorCombinations } from '../utils/colorCombinations';
import fontList from '../utils/fontList';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CryptoJS from 'crypto-js';


const isValidUrl = (url) => {
    try {
        new URL(url);
    } catch (e) {
        return false;
    }
    return true;
};


const UserProfileImage = ({ userId, perfilPicture }) => {
    const defaultAvatarUrl = `${process.env.REACT_APP_AVATAR_BASE_URL}${userId}${process.env.REACT_APP_AVATAR_PARAMS}`;
    const imageUrl = isValidUrl(perfilPicture) ? perfilPicture : defaultAvatarUrl;
    return (
        <div className="user-profile-image" style={{ backgroundImage: `url(${imageUrl})` }}></div>
    );
};




const GrowthPilot = () => {
    console.log('[GrowthPilot] Iniciando renderização do componente');

    const location = useLocation();
    const navigate = useNavigate();
    const supportedLanguages = ['pt', 'en'];
    const defaultLanguage = 'en';

    // Log para verificar variáveis de ambiente
    console.log('[GrowthPilot] Variáveis de ambiente:', {
        API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
        AVATAR_BASE_URL: process.env.REACT_APP_AVATAR_BASE_URL,
        AVATAR_PARAMS: process.env.REACT_APP_AVATAR_PARAMS
    });

    // Log para verificar localStorage
    console.log('[GrowthPilot] Dados do localStorage:', {
        userId: localStorage.getItem('userId'),
        totenId: localStorage.getItem('totenId'),
        accessToken: localStorage.getItem('accessToken'),
        language: localStorage.getItem('language')
    });

    // Define o idioma com base no navegador ou retorna ao padrão se não suportado
    const browserLanguage = navigator.language.slice(0, 2);
    const initialLanguage = supportedLanguages.includes(browserLanguage) ? browserLanguage : defaultLanguage;

    const [language, setLanguage] = useState(localStorage.getItem('language') || initialLanguage);
    const t = (screen, key) => translations[language][screen][key];
    const [isOpen, setIsOpen] = useState(false);

    const [isPciActive, setIsPciActive] = useState(false);
    const [isInstagramConnected, setIsInstagramConnected] = useState(false);
    const [tokenExpiriedFace, setTokenExpiriedFace] = useState(false);
    const [summary, setSummary] = useState('');
    const [calendarData, setCalendarData] = useState(null);
    const [isCalendarLoaded, setIsCalendarLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isNavigating, setIsNavigating] = useState(false);
    const [calendarCache, setCalendarCache] = useState(new Map());
    const [showCacheIndicator, setShowCacheIndicator] = useState(false);
    
    // Estados para navegação do calendário
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [navigationInfo, setNavigationInfo] = useState({
        canNavigatePrevious: false,
        canNavigateNext: false,
        userRegistrationMonth: 0,
        userRegistrationYear: new Date().getFullYear()
    });
    const [selectedPost, setSelectedPost] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [editedPost, setEditedPost] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [calendarRender, setCalendarRender] = useState(null);

    // Estados para seleção de cores e fontes (apenas para editor de posts)
    const [postStyleConfig, setPostStyleConfig] = useState({
        colors: {
            Titulos: '#FFFFFF',
            Textos: '#FFFFFF',
            Fundo: '#000000'
        },
        fonts: {
            titleFont: fontList[0]?.value || '',
            textFont: fontList[0]?.value || ''
        },
        useCustomStyle: false,
        comboId: colorCombinations[0]?.comboId || null,
        styleOption: 'templates',
        aspectRatio: '1:1'
    });

    const [showColorPicker, setShowColorPicker] = useState(false);
    const [selectedColorType, setSelectedColorType] = useState(null);
    const [fontsReady, setFontsReady] = useState(false);
    
    // Estados para preview (apenas para editor de posts)
    const [postPreviewColors, setPostPreviewColors] = useState({
        Titulos: '#FFFFFF',
        Textos: '#FFFFFF',
        Fundo: '#000000'
    });
    const [postFonts, setPostFonts] = useState({
        titleFont: fontList[0]?.value || '',
        textFont: fontList[0]?.value || ''
    });

    // Estados para templates (apenas para editor de posts)
    const [templates, setTemplates] = useState([]);
    const [postSelectedTemplate, setPostSelectedTemplate] = useState(null);
    const [postCurrentTemplateSlides, setPostCurrentTemplateSlides] = useState({});
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    // Estados para o popup de edição de imagens
    const [showImageEditor, setShowImageEditor] = useState(false);
    const [currentSlideId, setCurrentSlideId] = useState(null);
    const [imageSourceType, setImageSourceType] = useState('ai'); // Definir 'ai' como padrão
    const [selectedAIImage, setSelectedAIImage] = useState(null);
    const [selectedUploadImage, setSelectedUploadImage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [thumbnailImages, setThumbnailImages] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [previewGenerated, setPreviewGenerated] = useState(false);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [carouselEdit, setCarouselEdit] = useState(false);
    
    // Estado para controlar se está em modo de edição (individualizado por post)
    const [editingPostId, setEditingPostId] = useState(null);
    
    // Estado para controlar o loading da verificação
    const [isVerifyingUpdates, setIsVerifyingUpdates] = useState(false);
    
    // Estado para controlar qual plataforma foi escolhida para edição
    const [editingPlatform, setEditingPlatform] = useState(null);

    // Verificar o parâmetro 'lang' na URL e definir a linguagem com base nele
    const params = new URLSearchParams(location.search);
    const langFromUrl = params.get('lang');
    const userId = localStorage.getItem('userId');
    const accessToken = localStorage.getItem('accessToken');
    const totenId = localStorage.getItem('totenId');

    const [isMobile] = useMediaQuery('(max-width: 768px)');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    // Estados de configurações removidos - agora gerenciados na tela exclusiva de configurações

    useEffect(() => {
        if (langFromUrl && langFromUrl !== language) {
            setLanguage(langFromUrl);
            localStorage.setItem('language', langFromUrl);
        }
    }, [langFromUrl, language]);

    // Verificar status do PCI
    useEffect(() => {
        const fetchPCI = async () => {
            console.log('[GrowthPilot] Iniciando verificação do PCI');
            if (!userId || !totenId) {
                console.log('[GrowthPilot] Erro: userId ou totenId não encontrados');
                setIsPciActive(false);
                return;
            }

            try {
                console.log('[GrowthPilot] Fazendo requisição para PCI');
                const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/pciControl/summary`, {
                    userId,
                    totenId
                });

                console.log('[GrowthPilot] Resposta do PCI:', response.data);

                let summary = response.data.summary.replace(/\n\n/g, '   ').replace(/\n/g, '   ');

                if (summary) {
                    setSummary(summary);
                    setIsPciActive(true);
                    console.log('[GrowthPilot] PCI ativado com sucesso');
                } else {
                    setIsPciActive(false);
                    console.log('[GrowthPilot] PCI não ativado - sem summary');
                }
            } catch (error) {
                console.error('[GrowthPilot] Erro ao verificar status do PCI:', error);
                setIsPciActive(false);
            }
        };

        fetchPCI();
    }, [userId, totenId]);

    // Verificar status da conexão com Instagram
    useEffect(() => {
        // Verifica validade da conexão com Instagram
        const tokenExpiry = localStorage.getItem('tokenExpiry');

        if (!tokenExpiry || tokenExpiry === 'undefined' || tokenExpiry === 'null' || new Date(tokenExpiry) < new Date()) {
            setIsInstagramConnected(false);  // Instagram não está conectado
            setTokenExpiriedFace(true);
        } else {
            setIsInstagramConnected(true);  // Instagram está conectado
            setTokenExpiriedFace(false);
        }
    }, []);

    // Função para obter o mês e ano atuais
    const getCurrentMonthAndYear = () => {
        const now = new Date();
        return {
            month: now.getMonth(),
            year: now.getFullYear()
        };
    };

    // Função para ir para o mês atual
    const goToCurrentMonth = () => {
        const { month, year } = getCurrentMonthAndYear();
        console.log('[GrowthPilot] Navegando para o mês atual:', month, 'ano:', year);
        
        // Verificar se já está no mês atual
        if (currentMonth === month && currentYear === year) {
            console.log('[GrowthPilot] Já está no mês atual');
            return;
        }
        
        setCurrentMonth(month);
        setCurrentYear(year);
        fetchCalendarData(month, year, false);
    };

    // Carregar dados do calendário quando o componente for montado
    useEffect(() => {
        if (userId && totenId) {
            console.log('[GrowthPilot] Iniciando carregamento do calendário');
            const { month, year } = getCurrentMonthAndYear();
            
            // Atualizar estados para o mês atual
            setCurrentMonth(month);
            setCurrentYear(year);
            
            console.log('[GrowthPilot] Carregando mês atual:', month, 'ano:', year);
            fetchCalendarData(month, year, true);
        } else {
            console.log('[GrowthPilot] Não foi possível carregar calendário - userId ou totenId ausentes');
        }
    }, [userId, totenId]);

    // useEffect para renderizar o calendário quando os dados mudarem
    useEffect(() => {
        if (calendarData && calendarData.length > 0 && isCalendarLoaded) {
            const renderCalendarAsync = async () => {
                try {
                    const renderedCalendar = await renderCalendar();
                    setCalendarRender(renderedCalendar);
                } catch (error) {
                    console.error('[GrowthPilot] Erro ao renderizar calendário:', error);
                    setCalendarRender(null);
                }
            };
            renderCalendarAsync();
        } else {
            setCalendarRender(null);
        }
    }, [calendarData, isCalendarLoaded, currentMonth, currentYear]);

    // useEffect para limpar cache quando userId ou totenId mudarem (logout/login)
    useEffect(() => {
        if (!userId || !totenId) {
            clearCalendarCache();
        }
    }, [userId, totenId]);

    // useEffect para verificar se está no mês atual quando o componente é montado
    useEffect(() => {
        if (userId && totenId && isCalendarLoaded) {
            const { month, year } = getCurrentMonthAndYear();
            
            // Se não estiver no mês atual, navegar automaticamente
            if (currentMonth !== month || currentYear !== year) {
                console.log('[GrowthPilot] Detectado que não está no mês atual. Navegando automaticamente...');
                console.log('[GrowthPilot] Mês atual:', month, 'Ano atual:', year);
                console.log('[GrowthPilot] Mês atual do estado:', currentMonth, 'Ano atual do estado:', currentYear);
                
                // Pequeno delay para garantir que o componente já foi renderizado
                setTimeout(() => {
                    goToCurrentMonth();
                }, 100);
            }
        }
    }, [userId, totenId, isCalendarLoaded]);

        const fetchCalendarData = async (targetMonth = currentMonth, targetYear = currentYear, isInitialLoad = false) => {
        console.log('[GrowthPilot] Iniciando fetchCalendarData');
        
        // Verificar cache primeiro
        const cacheKey = `${targetYear}-${targetMonth}`;
        const cachedData = calendarCache.get(cacheKey);
        
        if (cachedData && !isInitialLoad) {
            console.log('[GrowthPilot] Dados encontrados no cache para', cacheKey);
            setNavigationInfo(cachedData.navigation);
            setCurrentMonth(cachedData.navigation.currentMonth);
            setCurrentYear(cachedData.navigation.currentYear);
            setCalendarData(cachedData.posts);
            setIsCalendarLoaded(true);
            
            // Mostrar indicador de cache
            setShowCacheIndicator(true);
            setTimeout(() => setShowCacheIndicator(false), 2000);
            
            console.log('[GrowthPilot] Cache hit! Dados carregados instantaneamente para', cacheKey);
            console.log('[GrowthPilot] Tamanho atual do cache:', calendarCache.size, 'meses');
            
            return;
        }
        
        // Definir loading apropriado
        if (isInitialLoad) {
            setIsLoading(true);
        } else {
            setIsNavigating(true);
        }
        
        try {
            console.log('[GrowthPilot] Fazendo requisição para calendário com navegação');
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/get-with-navigation`, {
                userId,
                totenId,
                targetMonth,
                targetYear
            });

            console.log('[GrowthPilot] Dados do calendário recebidos:', response.data);

            if (response.data && response.data.success) {
                // Atualizar informações de navegação
                setNavigationInfo(response.data.navigation);
                
                // Atualizar mês e ano atuais
                setCurrentMonth(response.data.navigation.currentMonth);
                setCurrentYear(response.data.navigation.currentYear);

                if (response.data.posts && response.data.posts.length > 0) {
                    // Garantir que todos os campos necessários estejam presentes
                    const processedData = response.data.posts.map(item => {
                    // Processar o campo content que pode ser um objeto
                    let processedContent = item.content;
                    if (typeof item.content === 'object' && item.content !== null) {
                        // Se for um objeto, extrair o texto relevante (legenda ou text)
                        processedContent = item.content.legenda || item.content.text || '';
                    }

                    return {
                        ...item,
                        content: processedContent,
                        title: item.title || '',
                        theme: item.theme || '',
                        status: item.status || 'gerado',
                        aiProposed: item.aiProposed || false,
                        aiSuggested: item.aiSuggested || false,
                        date: item.date || new Date(),
                        day: item.day || new Date(item.date).getDate(),
                        volume: item.volume || 0,
                        difficulty: item.difficulty || 0,
                        objetivo: item.objetivo || '',
                        funil: item.funil || '',
                        calendarId: item.calendarId || ''
                    };
                });

                console.log('[GrowthPilot] Dados processados do calendário:', processedData);
                setCalendarData(processedData);
                setIsCalendarLoaded(true);
                
                // Armazenar no cache
                const cacheData = {
                    navigation: response.data.navigation,
                    posts: processedData
                };
                setCalendarCache(prevCache => {
                    const newCache = new Map(prevCache);
                    newCache.set(cacheKey, cacheData);
                    return newCache;
                });
                console.log('[GrowthPilot] Dados armazenados no cache para', cacheKey);
                console.log('[GrowthPilot] Tamanho atual do cache:', calendarCache.size + 1, 'meses');
            } else {
                console.log('[GrowthPilot] Nenhum dado recebido do calendário');
                setCalendarData(null);
                setIsCalendarLoaded(false);
            }
            } else {
                console.log('[GrowthPilot] Resposta inválida do servidor');
                setCalendarData(null);
                setIsCalendarLoaded(false);
            }
        } catch (error) {
            console.error('[GrowthPilot] Erro ao carregar dados do calendário:', error);
            setCalendarData(null);
            setIsCalendarLoaded(false);
        } finally {
            if (isInitialLoad) {
                setIsLoading(false);
            } else {
                setIsNavigating(false);
            }
        }
    };

    // Funções de navegação do calendário
    const handlePreviousMonth = () => {
        if (navigationInfo.canNavigatePrevious) {
            let newMonth = currentMonth - 1;
            let newYear = currentYear;
            
            if (newMonth < 0) {
                newMonth = 11;
                newYear = currentYear - 1;
            }
            
            console.log('Navegando para mês anterior:', { newMonth, newYear, currentMonth, currentYear });
            fetchCalendarData(newMonth, newYear, false);
        }
    };

    const handleNextMonth = () => {
        if (navigationInfo.canNavigateNext) {
            let newMonth = currentMonth + 1;
            let newYear = currentYear;
            
            if (newMonth > 11) {
                newMonth = 0;
                newYear = currentYear + 1;
            }
            
            fetchCalendarData(newMonth, newYear, false);
        }
    };

    const handleHamburguerClick = () => {
        if (isMobile) {
            setIsSidebarOpen(prev => !prev);
        }
    };

    const handleSidebarToggle = () => {
        setIsSidebarCollapsed(prev => !prev);
    };

    const handleCloseDrawer = () => {
        setIsOpen(false); // Fecha o Drawer
    };

    const handleLanguageChange = (newLanguage) => {
        if (newLanguage !== language) {
            setLanguage(newLanguage);
            localStorage.setItem('language', newLanguage);
            navigate(`${location.pathname}?lang=${newLanguage}`);
        }
    };


    const perfilOpen = () => {
        navigate(`/viewuser?lang=${language}&from=growthpilot`);
    }

    const historyOpen = (language) => {
        navigate(`/history?lang=${language}&from=growthpilot`);
    }

    const logout = (language) => {
        // Limpar cache do calendário
        setCalendarCache(new Map());
        navigate(`/login?lang=${language}`);
    }

    // Função para limpar cache do calendário
    const clearCalendarCache = () => {
        setCalendarCache(new Map());
        console.log('[GrowthPilot] Cache do calendário limpo');
    };

    const mainOpen = (language) => {
        navigate(`/main?lang=${language}`);
    }

    const pciOpen = (language) => {
        navigate(`/pci?lang=${language}&from=growthpilot`);
    }

    const automacaoOpen = (language) => {
        navigate(`/automacao?lang=${language}&from=growthpilot`);
    }

    const handleTestAgent = async () => {
        try {
            setIsLoading(true);
            console.log('Iniciando teste do debate-calendar com totenId:', totenId);
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/agents/debate-calendar`, {
                totenId: totenId,
                userId: userId
            });

            console.log('Resposta do debate-calendar:', response.data);

            // Mostrar mensagem de sucesso
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Calendar loaded successfully',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });

            // Recarregar dados do calendário após o teste (sempre no mês atual)
            const { month, year } = getCurrentMonthAndYear();
            setCurrentMonth(month);
            setCurrentYear(year);
            fetchCalendarData(month, year, true);
        } catch (error) {
            console.error('Erro no teste do debate-calendar:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message,
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
            setIsLoading(false);
        }
    };


    // Função para voltar ao calendário
    const handleBackToCalendar = () => {
        setIsEditMode(false);
        setEditingPost(null);
        setEditedPost(null);
        setActiveTab('overview');
    };

    // Função para salvar as alterações no post
    const handleSavePost = async (updatedPost) => {
        try {
            // Preparar os dados do post para salvar
            const postToSave = {
                ...updatedPost,
                // Campos específicos do post
                ctaPadrao: updatedPost.ctaPadrao || '',
                respostaPublicaPadrao: updatedPost.respostaPublicaPadrao || '',
                mensagemPrivadaPadrao: updatedPost.mensagemPrivadaPadrao || '',
                link: updatedPost.link || '',
                textoBotao: updatedPost.textoBotao || '',
                horariosPadrao: updatedPost.horarioPadrao ? [updatedPost.horarioPadrao] : [],
                // Campos da aba Estilo e Cores - usar estados separados do editor
                colors: postStyleConfig.colors,
                titleFont: postStyleConfig.fonts.titleFont,
                textFont: postStyleConfig.fonts.textFont,
                templateTamanho: postStyleConfig.aspectRatio,
                templateTipo: postSelectedTemplate
            };

            // Chamada para salvar no servidor
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/update`, {
                userId: userId,
                totenId: totenId,
                post: postToSave
            });

            // Atualizar o estado local
            setCalendarData(prevData =>
                prevData.map(item =>
                    item.date === updatedPost.date ? postToSave : item
                )
            );

            // Invalidar cache do mês atual
            const cacheKey = `${currentYear}-${currentMonth}`;
            setCalendarCache(prevCache => {
                const newCache = new Map(prevCache);
                newCache.delete(cacheKey);
                return newCache;
            });
            console.log('[GrowthPilot] Cache invalidado para', cacheKey);

            setIsEditMode(false);
            setEditingPost(null);
            setEditedPost(null);
            setActiveTab('overview');

            Swal.fire({
                icon: 'success',
                title: 'Sucesso',
                text: 'Post salvo com sucesso!',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
        } catch (error) {
            console.error('Erro ao salvar post:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao salvar o post',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
        }
    };

    // Função para lidar com o clique na célula do calendário
    const handleCellClick = async (post) => {
        try {
            setIsLoading(true);

            // Debug para ver quais campos existem no post
            console.log('Post clicado:', post);
            console.log('Campos do post:', Object.keys(post));

            // Se o post já tem um id, tentar buscar o post completo
            if (post.id) {
                try {
                    const requestParams = {
                        userId: localStorage.getItem('userId'),
                        totenId: localStorage.getItem('totenId'),
                        calendarPostId: post.id,
                        calendarId: post.calendarId
                    };

                    console.log('Buscando post existente:', requestParams);

                    const postResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/get-post`, requestParams);

                    if (postResponse.data.success && postResponse.data.post) {
                        const fullPostData = postResponse.data.post;
                        console.log('Post encontrado:', fullPostData);

                        // Buscar configurações do perfil
                        const settingsResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/get-profile-settings`, {
                            userId: localStorage.getItem('userId'),
                            totenId: localStorage.getItem('totenId')
                        });

                        let settings = {};
                        if (settingsResponse.data.success && settingsResponse.data.settings) {
                            settings = settingsResponse.data.settings;
                        }

                        // Post encontrado, abrir editor
                        console.log('Post encontrado, abrindo editor');
                        const combinedPost = {
                            ...post,
                            ...fullPostData,
                            date: post.date,
                            calendarId: post.calendarId,
                            // Usar valores próprios do post se existirem, caso contrário usar configurações
                            horarioPadrao: fullPostData.horariosPadrao?.[0] || settings.horariosPadrao?.[0] || '',
                            ctaPadrao: fullPostData.ctaPadrao || settings.ctaPadrao || '',
                            respostaPublicaPadrao: fullPostData.respostaPublicaPadrao || settings.respostaPublicaPadrao || '',
                            mensagemPrivadaPadrao: fullPostData.mensagemPrivadaPadrao || settings.mensagemPrivadaPadrao || '',
                            link: fullPostData.link || settings.link || '',
                            textoBotao: fullPostData.textoBotao || settings.textoBotao || '',
                            // Campos da aba Estilo e Cores - usar dados do post ou configurações, não estados locais
                            colors: (fullPostData.colors && fullPostData.colors.Titulos)
                                ? fullPostData.colors
                                : (settings.colors && settings.colors.Titulos)
                                    ? settings.colors
                                    : {
                                        Titulos: '#FFFFFF',
                                        Textos: '#FFFFFF',
                                        Fundo: '#000000'
                                    },
                            titleFont: fullPostData.titleFont || settings.titleFont || '',
                            textFont: fullPostData.textFont || settings.textFont || '',
                            templateTamanho: fullPostData.templateTamanho || settings.templateTamanho || '',
                            templateTipo: fullPostData.templateTipo || settings.templateTipo || ''
                        };

                        // Inicializar os estados separados do editor com os dados do post
                        setPostStyleConfig({
                            colors: combinedPost.colors,
                            fonts: {
                                titleFont: combinedPost.titleFont || '',
                                textFont: combinedPost.textFont || ''
                            },
                            useCustomStyle: false,
                            comboId: null,
                            styleOption: 'templates',
                            aspectRatio: combinedPost.templateTamanho || '1:1'
                        });
                        setPostPreviewColors(combinedPost.colors);
                        setPostFonts({
                            titleFont: combinedPost.titleFont || '',
                            textFont: combinedPost.textFont || ''
                        });
                        setPostSelectedTemplate(combinedPost.templateTipo || '');

                        // Verificar se o post já tem presentationId e imagens
                        if (fullPostData.presentationId && fullPostData.tweets && fullPostData.tweets.some(tweet => tweet.slidePreview)) {
                            // Post já tem preview, definir carouselEdit e mostrar preview
                            setCarouselEdit(fullPostData.presentationId);
                            setPreviewGenerated(true);
                        }

                        setEditingPost(combinedPost);
                        setEditedPost(combinedPost);
                        setActiveTab('overview');
                        setIsEditMode(true);
                        setIsLoading(false);
                        return;
                    }
                } catch (error) {
                    console.log('Post não encontrado ou erro na busca:', error.message);
                }
            }

            // Se chegou aqui, significa que não encontrou o post ou o post não tem postId
            // Iniciar processo de criação de novo post
            console.log('Iniciando criação de novo post para a data:', post.date);

            // Buscar configurações do perfil
            const settingsResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/get-profile-settings`, {
                userId: localStorage.getItem('userId'),
                totenId: localStorage.getItem('totenId')
            });

            let settings = {};
            if (settingsResponse.data.success && settingsResponse.data.settings) {
                settings = settingsResponse.data.settings;
            }

            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/agents/carrossel`, {
                totenId: localStorage.getItem('totenId'),
                userId: localStorage.getItem('userId'),
                theme: post.theme || post.title || `Conteúdo para ${post.day}/${new Date(post.date).getMonth() + 1}`,
                calendarId: post.calendarId,
                calendarPostId: post.id
            });

            // Preparar dados para edição
            const generatedPost = {
                ...post,
                ...response.data,
                id: response.data.postId,
                status: 'pendente_postagem',
                date: post.date,
                day: post.day,
                calendarId: post.calendarId,
                // Para novo post, usar apenas configurações
                horarioPadrao: settings.horariosPadrao?.[0] || '',
                ctaPadrao: settings.ctaPadrao || '',
                respostaPublicaPadrao: settings.respostaPublicaPadrao || '',
                mensagemPrivadaPadrao: settings.mensagemPrivadaPadrao || '',
                link: settings.link || '',
                textoBotao: settings.textoBotao || ''
            };

            console.log('Novo post gerado:', generatedPost);

            setEditingPost(generatedPost);
            setEditedPost(generatedPost);
            setActiveTab('overview');
            setIsEditMode(true);

            // Atualizar o calendário com o novo post
            setCalendarData(prevData =>
                prevData.map(item =>
                    item.date === post.date ? { ...item, ...generatedPost } : item
                )
            );

        } catch (error) {
            console.error('Erro ao processar clique na célula:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: error.message || 'Erro ao carregar/gerar conteúdo',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
        } finally {
            setIsLoading(false);
        }
    };


    // Função para renderizar o calendário
    const renderCalendar = async () => {
        console.log('[GrowthPilot] Iniciando renderização do calendário');
        if (!calendarData || calendarData.length === 0) {
            console.log('[GrowthPilot] Sem dados para renderizar o calendário');
            return null;
        }

        console.log('[GrowthPilot] Renderizando calendário com dados:', calendarData);

        // Dias da semana
        const weekDays = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

        // Função para renderizar o status correto
        const renderStatus = (post) => {
            // Obter o objetivo do post
            let objetivo = post.objetivo || post.objective || post.theme || "Sem objetivo";

            // Substituir "Awareness" por "Conhecimento"
            objetivo = objetivo.replace(/Awareness/g, "Conhecimento");

            // Se contém barra, pegar apenas o primeiro termo
            if (objetivo.includes('/')) {
                const [primeiraParte] = objetivo.split('/');
                return (
                    <div className="post-objective">
                        {primeiraParte.trim()}
                    </div>
                );
            }

            return (
                <div className="post-objective">
                    {objetivo}
                </div>
            );
        };

        // Função para mostrar a posição do funil
        const renderFunnelPosition = (post) => {
            if (!post.funil && !post.funnelPosition) return null;

            // Obter a posição do funil
            let posicao = post.funil || post.funnelPosition || "";

            // Remover asteriscos
            posicao = posicao.replace(/\*\*/g, "").trim();

            return (
                <div className="funnel-position">
                    {posicao}
                </div>
            );
        };

        // Ordenar os posts por data
        const sortedPosts = [...calendarData].sort((a, b) => new Date(a.date) - new Date(b.date));

        // Criar um array com os nomes dos meses em português
        const monthNames = [
            "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
            "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
        ];

        // Calcular o primeiro dia do mês atual
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
        
        // Calcular o último dia do mês atual
        const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
        
        // Calcular quantas semanas o mês tem (incluindo dias do mês anterior/sucessor)
        const firstDayOfWeek = firstDayOfMonth.getDay(); // 0 = Domingo, 1 = Segunda, etc.
        const daysInMonth = lastDayOfMonth.getDate();
        
        // Calcular total de células necessárias (incluindo dias do mês anterior)
        const totalCells = firstDayOfWeek + daysInMonth;
        const totalWeeks = Math.ceil(totalCells / 7);
        
        // LÓGICA BASEADA NA DATA DE CRIAÇÃO DO PRIMEIRO CALENDÁRIO GROWTHPILOT
        // Buscar a data de criação do primeiro calendário do GrowthPilot
        let firstCalendarDate = null;
        
        try {
            const userId = localStorage.getItem('userId');
            const totenId = localStorage.getItem('totenId');
            
            if (userId && totenId) {
                const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/get-first-calendar`, {
                    userId: userId,
                    totenId: totenId
                });
                
                if (response.data.success && response.data.firstCalendar) {
                    firstCalendarDate = new Date(response.data.firstCalendar.calendarStart);
                    console.log(`[CALENDAR] Primeiro calendário do GrowthPilot encontrado: ${firstCalendarDate.toISOString()}`);
                } else {
                    console.log(`[CALENDAR] Nenhum calendário do GrowthPilot encontrado`);
                }
            }
        } catch (error) {
            console.error('[CALENDAR] Erro ao buscar primeiro calendário:', error);
        }
        
     
        
        const activationMonth = firstCalendarDate.getMonth();
        const activationYear = firstCalendarDate.getFullYear();
        const activationDay = firstCalendarDate.getDate();
        
        console.log(`[CALENDAR] Data de referência (primeiro calendário): ${firstCalendarDate.toISOString()}`);
        console.log(`[CALENDAR] Mês de ativação: ${activationMonth + 1}/${activationYear}`);
        console.log(`[CALENDAR] Dia de ativação: ${activationDay}`);
        
        // Calcular semanas baseado na data de criação do primeiro calendário
        let weeksToShow; // Padrão: 2 semanas
        
        if (currentYear === activationYear && currentMonth === activationMonth) {
            // Mês do primeiro calendário: calcular dinamicamente as semanas a partir da semana do primeiro calendário
            const daysFromStart = firstDayOfWeek + activationDay - 1;
            const activationWeek = Math.ceil(daysFromStart / 7);
            
            // Calcular quantas semanas restam no mês a partir da semana do primeiro calendário
            const weeksFromActivation = totalWeeks - activationWeek + 1;
            weeksToShow = Math.max(1, weeksFromActivation); // Mínimo de 1 semana
            
            console.log(`[CALENDAR] Mês do primeiro calendário - dia ${activationDay}, primeiro dia da semana: ${firstDayOfWeek}, dias desde início: ${daysFromStart}, semana: ${activationWeek}`);
            console.log(`[CALENDAR] Total de semanas no mês: ${totalWeeks}, semanas a partir da ativação: ${weeksFromActivation}, mostrando: ${weeksToShow} semanas`);
        } else {
            // Meses posteriores: calendário completo
            weeksToShow = totalWeeks;
            console.log(`[CALENDAR] Mês posterior ao primeiro calendário - mostrando ${weeksToShow} semanas (calendário completo)`);
        }
        
        console.log(`[CALENDAR] Mês: ${currentMonth + 1}/${currentYear}`);
        console.log(`[CALENDAR] Dias no mês: ${daysInMonth}`);
        console.log(`[CALENDAR] Total de semanas possíveis: ${totalWeeks}`);
        console.log(`[CALENDAR] Semanas a mostrar: ${weeksToShow}`);

        // Criar array de todos os dias do calendário (incluindo dias do mês anterior)
        const calendarDays = [];
        
        // Para o mês do primeiro calendário, calcular a partir de qual dia começar
        let startDay = 1;
        let startWeek = 1;
        let endDay = daysInMonth;
        
        if (currentYear === activationYear && currentMonth === activationMonth) {
            // Mês do primeiro calendário: começar apenas da semana do primeiro calendário
            const daysFromStart = firstDayOfWeek + activationDay - 1;
            startWeek = Math.ceil(daysFromStart / 7);
            
            // Calcular qual dia do mês corresponde ao início da semana do primeiro calendário
            const daysToSkip = (startWeek - 1) * 7 - firstDayOfWeek;
            startDay = Math.max(1, daysToSkip + 1);
            
            // Para o mês do primeiro calendário, mostrar até o fim do mês (não apenas uma semana)
            endDay = daysInMonth;
            
            console.log(`[CALENDAR] Mês do primeiro calendário - começando da semana ${startWeek}, dia ${startDay} até ${endDay}`);
        }
        
        // Adicionar dias do mês anterior (se necessário e se não for mês do primeiro calendário)
        if (currentYear !== activationYear || currentMonth !== activationMonth) {
            const previousMonth = new Date(currentYear, currentMonth, 0);
            const daysInPreviousMonth = previousMonth.getDate();
            
            for (let i = firstDayOfWeek - 1; i >= 0; i--) {
                const day = daysInPreviousMonth - i;
                const date = new Date(currentYear, currentMonth - 1, day);
                calendarDays.push({
                    date: date,
                    day: day,
                    month: date.getMonth(),
                    year: date.getFullYear(),
                    dayOfWeek: date.getDay(),
                    post: null, // Não há posts para dias do mês anterior
                    isCurrentMonth: false
                });
            }
        }
        
        // Adicionar dias do mês atual (a partir do dia calculado para mês do primeiro calendário)
        for (let day = startDay; day <= endDay; day++) {
            const date = new Date(currentYear, currentMonth, day);
            
            // Verificar se o dia é anterior à data do primeiro calendário
            const isBeforeActivation = currentYear === activationYear && 
                                     currentMonth === activationMonth && 
                                     day < activationDay;
            
            // Encontrar post correspondente a este dia
            const post = sortedPosts.find(p => {
                const postDate = new Date(p.date);
                return postDate.getDate() === day && 
                       postDate.getMonth() === currentMonth && 
                       postDate.getFullYear() === currentYear;
            });
            
            calendarDays.push({
                date: date,
                day: day,
                month: date.getMonth(),
                year: date.getFullYear(),
                dayOfWeek: date.getDay(),
                post: post || null,
                isCurrentMonth: true,
                isBeforeActivation: isBeforeActivation
            });
        }
        
        // Adicionar dias do próximo mês para completar as semanas necessárias
        const totalCellsNeeded = weeksToShow * 7;
        const remainingCells = totalCellsNeeded - calendarDays.length;
        
        for (let day = 1; day <= remainingCells; day++) {
            const date = new Date(currentYear, currentMonth + 1, day);
            calendarDays.push({
                date: date,
                day: day,
                month: date.getMonth(),
                year: date.getFullYear(),
                dayOfWeek: date.getDay(),
                post: null, // Não há posts para dias do próximo mês
                isCurrentMonth: false
            });
        }

        // Dividir os dias em semanas (apenas as necessárias)
        const weeks = [];
        for (let i = 0; i < weeksToShow; i++) {
            const weekDays = calendarDays.slice(i * 7, (i + 1) * 7);
            weeks.push(weekDays);
        }

        // Obter o mês e ano para o título do calendário
        const currentMonthName = monthNames[currentMonth];
        const currentYearDisplay = currentYear;

        // Função para renderizar um dia do calendário
        const renderDay = (dayInfo) => {
            const { date, day, dayOfWeek, post, isCurrentMonth, isBeforeActivation } = dayInfo;

            return (
                <div
                    className={`calendar-day-container ${!post ? 'empty' : ''} ${!isCurrentMonth ? 'other-month' : ''} ${isBeforeActivation ? 'before-activation' : ''}`}
                    key={`day-${day}-${date.getTime()}`}
                >
                    {post ? (
                        <>
                            <div className="day-header">
                                <div className="day-weekname">{weekDays[dayOfWeek]}</div>
                                <div className="day-number">{day}</div>
                            </div>

                            <div
                                className={`calendar-day ${post.status?.toLowerCase() === 'publicado' ? 'published' : ''}`}
                                onClick={() => handleCellClick(post)}
                                style={{ cursor: 'pointer' }}
                            >
                                {renderStatus(post)}

                                {(post.aiProposed || post.aiSuggested) && (
                                    <div className="ai-suggested">
                                        <FaRobot />
                                    </div>
                                )}

                                <div className="day-content-title">
                                    {post.title || post.theme || "Sem título"}
                                </div>

                                {renderFunnelPosition(post)}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="day-header">
                                <div className="day-weekname">{weekDays[dayOfWeek]}</div>
                                <div className="day-number">{day}</div>
                            </div>
                            <div className="calendar-day empty">
                                <div className="empty-cell-content">
                                    <span></span>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            );
        };

        return (
            <div className="calendar-container">
                <div className="calendar-grid">
                    {/* Cabeçalho do calendário dentro da grid */}
                    <div className="calendar-header">
                        <div className="calendar-navigation">
                            <button 
                                className={`nav-button ${!navigationInfo.canNavigatePrevious ? 'disabled' : ''}`}
                                onClick={handlePreviousMonth}
                                disabled={!navigationInfo.canNavigatePrevious}
                            >
                                <span>‹</span>
                            </button>
                            <h2>{currentMonthName} {currentYearDisplay}</h2>
                            <button 
                                className={`nav-button ${!navigationInfo.canNavigateNext ? 'disabled' : ''}`}
                                onClick={handleNextMonth}
                                disabled={!navigationInfo.canNavigateNext}
                            >
                                <span>›</span>
                            </button>
                            <button 
                                className={`nav-button today-button ${(() => {
                                    const { month, year } = getCurrentMonthAndYear();
                                    return (currentMonth === month && currentYear === year) ? 'current-month' : '';
                                })()}`}
                                onClick={goToCurrentMonth}
                                disabled={(() => {
                                    const { month, year } = getCurrentMonthAndYear();
                                    return (currentMonth === month && currentYear === year);
                                })()}
                                title="Ir para o mês atual"
                            >
                                Hoje
                            </button>
                        </div>
                    </div>

                    {/* Renderizar todas as semanas do mês */}
                    {weeks.map((weekDays, weekIndex) => (
                        <div className="calendar-week" key={`week-${weekIndex}`}>
                            {weekDays.map(dayInfo => renderDay(dayInfo))}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Componente de edição do post
    const renderPostEditor = () => {
        console.log('Renderizando editor com editedPost:', editedPost);

        const handleInputChange = (field, value) => {
            setEditedPost(prev => ({
                ...prev,
                [field]: value
            }));
        };

        const handleTweetChange = (index, value) => {
            setEditedPost(prev => ({
                ...prev,
                tweets: prev.tweets?.map((tweet, i) =>
                    i === index ? { ...tweet, conteudo: value } : tweet
                ) || []
            }));
        };

        const handleSave = () => {
            handleSavePost(editedPost);
        };

        return (
            <div className="post-editor">
                <div className="editor-header">
                    
                    <div className="editor-actions">
                       
                        <div className="editor-tabs" style={{ padding: '24px 0 24px 0', marginTop: 0 }}>
                            <button
                                className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                                onClick={() => setActiveTab('overview')}
                                style={{ padding: '14px 28px', fontSize: '16px' }}
                            >
                                Visão Geral
                            </button>
                            <button
                                className={`tab ${activeTab === 'slides' ? 'active' : ''}`}
                                onClick={() => setActiveTab('slides')}
                                style={{ padding: '14px 28px', fontSize: '16px' }}
                            >
                                Slides do Carrossel
                            </button>
                            <button
                                className={`tab ${activeTab === 'style' ? 'active' : ''}`}
                                onClick={() => setActiveTab('style')}
                                style={{ padding: '14px 28px', fontSize: '16px' }}
                            >
                                Estilo e Cores
                            </button>
                            <button
                                className={`tab ${activeTab === 'caption' ? 'active' : ''}`}
                                onClick={() => setActiveTab('caption')}
                                style={{ padding: '14px 28px', fontSize: '16px' }}
                            >
                                CTA & Legenda
                            </button>
                            <button
                                className={`tab ${activeTab === 'automation' ? 'active' : ''}`}
                                onClick={() => setActiveTab('automation')}
                                style={{ padding: '14px 28px', fontSize: '16px' }}
                            >
                                Automação
                            </button>
                            <button
                                className={`tab ${activeTab === 'preview' ? 'active' : ''}`}
                                onClick={() => setActiveTab('preview')}
                                style={{ padding: '14px 28px', fontSize: '16px' }}
                            >
                                Preview
                            </button>
                        </div>
                    </div>
                </div>

                <div className="editor-content">
                    {activeTab === 'overview' && (
                        <div className="tab-content">
                        <div className="form-row">
                            <div className="form-group form-group-half">
                                <label style={{ fontSize: '14px', color: '#757575', marginBottom: '18px', display: 'block', fontWeight: 600 }}>Tema:</label>
                                <input
                                    type="text"
                                    value={editedPost.theme || editedPost.ideiaEscolhidaTexto || ''}
                                    onChange={(e) => handleInputChange('theme', e.target.value)}
                                />
                            </div>
                            <div className="form-group form-group-half">
                                    <label style={{ fontSize: '14px', color: '#757575', marginBottom: '18px', display: 'block', fontWeight: 600 }}>Nicho:</label>
                                    <input
                                        type="text"
                                        value={editedPost.niche || ''}
                                        onChange={(e) => handleInputChange('niche', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                
                                <div className="form-group form-group-half">
                                    <label style={{ fontSize: '14px', color: '#757575', marginBottom: '18px', display: 'block', fontWeight: 600 }}>Status:</label>
                                    <select
                                        value={editedPost.status || 'pendente_postagem'}
                                        onChange={(e) => handleInputChange('status', e.target.value)}
                                    >
                                        <option value="pendente_postagem">Pendente de Postagem</option>
                                        <option value="em_aprovacao">Em Aprovação</option>
                                        <option value="publicado">Publicado</option>
                                        <option value="erro">Erro</option>
                                    </select>
                                </div>
                                <div className="form-group form-group-half">
                                    <label style={{ fontSize: '14px', color: '#757575', marginBottom: '18px', display: 'block', fontWeight: 600 }}>Horário de postagem:</label>
                                    <input
                                        type="text"
                                        value={editedPost.horarioPadrao || ''}
                                        onChange={(e) => handleInputChange('horarioPadrao', e.target.value)}
                                        placeholder="Ex: 09:00"
                                    />
                                </div>
                            </div>
                            {editedPost.historiaFinal && (
                                <div className="form-group">
                                    <label style={{ fontSize: '14px', color: '#757575', marginBottom: '18px', display: 'block', fontWeight: 600 }}>História Final:</label>
                                    <textarea
                                        value={editedPost.historiaFinal}
                                        onChange={(e) => handleInputChange('historiaFinal', e.target.value)}
                                        rows={6}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'slides' && (
                        <div className="tab-content">
                            <div className="slides-container">
                                {editedPost.tweets && editedPost.tweets.length > 0 ? (
                                    editedPost.tweets.map((tweet, index) => (
                                        <div key={index} className="slide-editor">
                                            <div className="slide-header">
                                                <label style={{ fontSize: '16px', color: '#757575', display: 'block', fontWeight: 600 }}>Slide {tweet.numero || index + 1}</label>
                                            </div>
                                            <div className="slide-content">
                                                <textarea
                                                    value={tweet.conteudo || ''}
                                                    onChange={(e) => handleTweetChange(index, e.target.value)}
                                                    placeholder={`Conteúdo do slide ${index + 1}`}
                                                    rows={3}
                                                />
                                                {tweet.urlImagem && (
                                                    <div className="slide-image-growthpilot" onClick={() => handleSlideImageClick(index + 1)}>
                                                        <img src={tweet.urlImagem} alt={`Slide ${index + 1}`} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-slides">
                                        <p>Nenhum slide disponível</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'caption' && (
                        <div className="tab-content">
                            <div className="form-group">
                                <label style={{ fontSize: '14px', color: '#757575', marginBottom: '18px', display: 'block', fontWeight: 600 }}>CTA Padrão:</label>
                                <input
                                    type="text"
                                    value={editedPost.ctaPadrao || ''}
                                    onChange={(e) => handleInputChange('ctaPadrao', e.target.value)}
                                    placeholder="Insira seu CTA padrão..."
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #e2e8f0',
                                        backgroundColor: 'white',
                                        borderRadius: '5px',
                                        fontSize: '15px',
                                        color: '#757575'
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ fontSize: '14px', color: '#757575', marginBottom: '18px', display: 'block', fontWeight: 600 }}>Legenda do Post:</label>
                                <textarea
                                    value={editedPost.legenda || ''}
                                    onChange={(e) => handleInputChange('legenda', e.target.value)}
                                    rows={10}
                                    placeholder="Digite a legenda que acompanhará o carrossel..."
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'automation' && (
                        <div className="tab-content">
                            <div className="form-group">
                                <label style={{ fontSize: '14px', color: '#757575', marginBottom: '12px', display: 'block', fontWeight: 600 }}>Resposta Pública Padrão:</label>
                                <input
                                    type="text"
                                    value={editedPost.respostaPublicaPadrao || ''}
                                    onChange={(e) => handleInputChange('respostaPublicaPadrao', e.target.value)}
                                    placeholder="Insira a resposta pública padrão..."
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #e2e8f0',
                                        backgroundColor: 'white',
                                        borderRadius: '5px',
                                        fontSize: '15px',
                                        color: '#757575',
                                        marginBottom: '7.5px'
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ fontSize: '14px', color: '#757575', marginBottom: '12px', display: 'block', fontWeight: 600 }}>Mensagem Privada Automática:</label>
                                <input
                                    type="text"
                                    value={editedPost.mensagemPrivadaPadrao || ''}
                                    onChange={(e) => handleInputChange('mensagemPrivadaPadrao', e.target.value)}
                                    placeholder="Insira a mensagem privada automática..."
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #e2e8f0',
                                        backgroundColor: 'white',
                                        borderRadius: '5px',
                                        fontSize: '15px',
                                        color: '#757575',
                                        marginBottom: '7.5px'
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ fontSize: '14px', color: '#757575', marginBottom: '12px', display: 'block', fontWeight: 600 }}>Link Externo:</label>
                                <input
                                    type="text"
                                    value={editedPost.link || ''}
                                    onChange={(e) => handleInputChange('link', e.target.value)}
                                    placeholder="Insira o link externo..."
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #e2e8f0',
                                        backgroundColor: 'white',
                                        borderRadius: '5px',
                                        fontSize: '15px',
                                        color: '#757575',
                                        marginBottom: '7.5px'
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label style={{ fontSize: '14px', color: '#757575', marginBottom: '12px', display: 'block', fontWeight: 600 }}>Texto do Botão:</label>
                                <input
                                    type="text"
                                    value={editedPost.textoBotao || ''}
                                    onChange={(e) => handleInputChange('textoBotao', e.target.value)}
                                    placeholder="Insira o texto do botão..."
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #e2e8f0',
                                        backgroundColor: 'white',
                                        borderRadius: '5px',
                                        fontSize: '15px',
                                        color: '#757575',
                                        marginBottom: '7.5px'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === 'style' && (
                        <div className="tab-content">
                            {/* Novo controle por radio button */}
                            <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', alignItems: 'center' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '14px', color: '#757575' }}>
                                    <input
                                        type="radio"
                                        name="styleOption"
                                        value="templates"
                                        checked={postStyleConfig.styleOption === 'templates'}
                                        onChange={() => setPostStyleConfig(prev => ({ ...prev, styleOption: 'templates' }))}
                                        style={{ accentColor: '#8E59FF' }}
                                    />
                                    Templates
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '14px', color: '#757575' }}>
                                    <input
                                        type="radio"
                                        name="styleOption"
                                        value="personalizado"
                                        checked={postStyleConfig.styleOption === 'personalizado'}
                                        onChange={() => setPostStyleConfig(prev => ({ ...prev, styleOption: 'personalizado' }))}
                                        style={{ accentColor: '#8E59FF' }}
                                    />
                                    Personalizado
                                </label>
                            </div>
                            {/* Conteúdo condicional */}
                            {postStyleConfig.styleOption === 'templates' && (
                                <div style={{ marginTop: '10px' }}>
                                    {postSelectedTemplate && (
                                        <div style={{
                                            padding: '12px',
                                            backgroundColor: '#f7fafc',
                                            borderRadius: '8px',
                                            marginBottom: '15px',
                                            border: '1px solid #e2e8f0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '24px'
                                        }}>
                                            <div style={{ fontSize: '14px', color: '#757575', fontWeight: '600' }}>
                                                {postSelectedTemplate}
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '18px' }}>
                                                <span style={{ fontWeight: 600, color: '#757575', fontSize: '14px' }}>Tamanho:</span>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500, fontSize: '14px', color: '#757575' }}>
                                                    <input
                                                        type="radio"
                                                        name="templateAspect"
                                                        value="1:1"
                                                        checked={postStyleConfig.aspectRatio === '1:1'}
                                                        onChange={() => setPostStyleConfig(prev => ({ ...prev, aspectRatio: '1:1' }))}
                                                        style={{ accentColor: '#8E59FF' }}
                                                    />
                                                    (1:1) Quadrado
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500, fontSize: '14px', color: '#757575' }}>
                                                    <input
                                                        type="radio"
                                                        name="templateAspect"
                                                        value="4:5"
                                                        checked={postStyleConfig.aspectRatio === '4:5'}
                                                        onChange={() => setPostStyleConfig(prev => ({ ...prev, aspectRatio: '4:5' }))}
                                                        style={{ accentColor: '#8E59FF' }}
                                                    />
                                                    (4:5) Retrato
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    <TemplateGrid />
                                </div>
                            )}
                            {postStyleConfig.styleOption === 'personalizado' && (
                                <>
                                    {/* Preview simples para o editor de posts */}
                                    <div style={{
                                        padding: '20px',
                                        borderRadius: '8px',
                                        backgroundColor: postPreviewColors.Fundo,
                                        border: '1px solid #e2e8f0',
                                        marginBottom: '20px',
                                        minHeight: '95px',
                                        height: '95px',
                                        transition: 'height 0.2s',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                            <h4 style={{
                                                color: postPreviewColors.Titulos,
                                                fontFamily: postFonts.titleFont ? `"${postFonts.titleFont}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : 'system-ui, sans-serif',
                                                margin: 0,
                                                fontWeight: '700'
                                            }}>
                                                Título do Post
                                            </h4>
                                            <MdRemoveRedEye style={{ color: postPreviewColors.Titulos }} />
                                        </div>
                                        <p style={{
                                            color: postPreviewColors.Textos,
                                            fontFamily: postFonts.textFont ? `"${postFonts.textFont}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : 'system-ui, sans-serif',
                                            margin: 0,
                                            fontWeight: '400'
                                        }}>
                                            Este é um exemplo de como ficará o texto do seu carrossel com as cores e fontes selecionadas.
                                        </p>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'flex-start', marginTop: '20px' }}>
                                        {/* Cores Personalizadas */}
                                        <div style={{ minWidth: '180px' }}>
                                            <label style={{ fontSize: '14px', color: '#757575', marginBottom: '18px', display: 'block', fontWeight: 600 }}>
                                                Cores Personalizadas
                                            </label>
                                            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                                                {['Titulos', 'Textos', 'Fundo'].map((colorType) => (
                                                    <div key={colorType} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                                        <span style={{ fontSize: '12px', color: '#757575' }}>{colorType}</span>
                                                        <div
                                                            style={{
                                                                width: '48px',
                                                                height: '48px',
                                                                borderRadius: '6px',
                                                                border: '1px solid #ddd',
                                                                cursor: 'pointer',
                                                                backgroundColor: postPreviewColors[colorType],
                                                                transition: 'transform 0.2s'
                                                            }}
                                                                                                                                        onClick={() => {
                                                                                setSelectedColorType(colorType);
                                                                                setShowColorPicker(true);
                                                                            }}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        {/* Fontes, uma embaixo da outra */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', flex: 1 }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '14px', color: '#757575', marginBottom: '18px', fontWeight: 600 }}>
                                                    Fonte do Título
                                                </label>
                                                <select
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        border: '1px solid #e2e8f0',
                                                        backgroundColor: 'white',
                                                        borderRadius: '5px',
                                                        fontSize: '15px',
                                                        color: '#757575'
                                                    }}
                                                    value={postFonts.titleFont || ''}
                                                    onChange={(e) => handlePostFontChange('titleFont', e.target.value)}
                                                >
                                                    <option value="" disabled>Selecionar fonte</option>
                                                    {fontList.filter(font => font.value).map(font => (
                                                        <option key={font.value} value={font.value}>
                                                            {font.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: '14px', color: '#757575', marginBottom: '18px', fontWeight: 600 }}>
                                                    Fonte do Texto
                                                </label>
                                                <select
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px',
                                                        border: '1px solid #e2e8f0',
                                                        backgroundColor: 'white',
                                                        borderRadius: '5px',
                                                        fontSize: '15px',
                                                        color: '#757575'
                                                    }}
                                                    value={postFonts.textFont || ''}
                                                    onChange={(e) => handlePostFontChange('textFont', e.target.value)}
                                                >
                                                    <option value="" disabled>Selecionar fonte</option>
                                                    {fontList.filter(font => font.value).map(font => (
                                                        <option key={font.value} value={font.value}>
                                                            {font.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                            {/* Modal do Color Picker */}
                            {showColorPicker && (
                                <ChakraProvider>
                                    <Modal isOpen={showColorPicker} onClose={() => setShowColorPicker(false)}>
                                        <ModalOverlay />
                                        <ModalContent>
                                            <ModalBody display="flex" justifyContent="center">
                                                <SketchPicker
                                                    color={postPreviewColors[selectedColorType]}
                                                    onChange={handlePostColorChange}
                                                />
                                            </ModalBody>
                                            <ModalFooter>
                                                <Button
                                                    onClick={() => setShowColorPicker(false)}
                                                    sx={{
                                                        backgroundColor: '#1a202c',
                                                        color: 'white',
                                                    }}
                                                >
                                                    Confirmar
                                                </Button>
                                            </ModalFooter>
                                        </ModalContent>
                                    </Modal>
                                </ChakraProvider>
                            )}
                        </div>
                    )}

                    {activeTab === 'preview' && (
                        <div className="tab-content">
                            {!previewGenerated ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '400px',
                                    padding: '40px 20px'
                                }}>
                                    <div style={{
                                        fontSize: '18px',
                                        color: '#757575',
                                        marginBottom: '30px',
                                        textAlign: 'center',
                                        fontWeight: 500
                                    }}>
                                        Clique no botão abaixo para gerar o preview do seu carrossel
                                    </div>
                                    <button
                                        onClick={handleGeneratePreview}
                                        disabled={isGeneratingPreview}
                                        style={{
                                            backgroundColor: 'black',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '15px 30px',
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            cursor: isGeneratingPreview ? 'not-allowed' : 'pointer',
                                            opacity: isGeneratingPreview ? 0.7 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px'
                                        }}
                                    >
                                        {isGeneratingPreview ? (
                                            <>
                                                <div className="spinner_4" style={{ margin: 0, width: '20px', height: '20px' }} />
                                                Gerando Preview...
                                            </>
                                        ) : (
                                            <>
                                                <MdRemoveRedEye size={20} />
                                                Gerar Preview
                                            </>
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: '100%',
                                    padding: '0 20px 0 20px', // remover espaço superior
                                    maxWidth: '900px',
                                    margin: '-25px auto',
                                    boxSizing: 'border-box',
                                   
                                }}>
                                    <div className="outerContainer" style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '100%',
                                        height: 'auto',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                    }}>
                                        <div className="innerContainer" style={{
                                            position: 'relative',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            flexWrap: 'nowrap',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            width: '100%',
                                            gap: '16px',
                                            overflowX: 'auto',
                                            overflowY: 'hidden',
                                            maxWidth: '100%',
                                            minHeight: '440px', // reduzir altura da janela
                                            height: '440px',    // reduzir altura da janela
                                            boxSizing: 'border-box',
                                            paddingLeft: '24px',
                                            paddingRight: '24px',
                                            paddingTop: 0,
                                            scrollSnapType: 'x mandatory'
                                        }}>
                                            {editedPost.tweets && editedPost.tweets.length > 0 ? (
                                                editedPost.tweets.map((tweet, index) => (
                                                    <img
                                                        key={index}
                                                        src={tweet.slidePreview || 'https://via.placeholder.com/220x220?text=Slide+' + (index + 1)}
                                                        alt={`Slide ${index + 1}`}
                                                        style={{
                                                            width: postStyleConfig.aspectRatio === '4:5' ? '255px' : '325px', // 10% maior
                                                            height: postStyleConfig.aspectRatio === '4:5' ? '325px' : '325px', // 10% maior
                                                            objectFit: 'cover',
                                                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                                            flexShrink: 0,
                                                            borderRadius: '8px',
                                                            background: '#f7f7f7',
                                                            scrollSnapAlign: 'start',
                                                            marginRight: index === editedPost.tweets.length - 1 ? '24px' : 0,
                                                            marginBottom: '-60px'
                                                        }}
                                                    />
                                                ))
                                            ) : (
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '100%',
                                                    height: '100%',
                                                    color: '#757575',
                                                    fontSize: '16px'
                                                }}>
                                                    Nenhum slide disponível para preview
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{
                                        marginTop: '18px', // espaço mínimo entre slides e botões
                                        display: 'flex',
                                        gap: '15px',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                    }}>
                                        <button
                                            onClick={editingPostId === carouselEdit ? handleVerifyClick : handleEditClick}
                                            disabled={isVerifyingUpdates}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                background: '#ede9fe',
                                                color: '#8E59FF',
                                                borderRadius: '8px',
                                                fontWeight: 500,
                                                fontSize: '16px',
                                                border: 'none',
                                                boxShadow: 'none',
                                                padding: '10px 20px',
                                                cursor: isVerifyingUpdates ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.3s ease',
                                                opacity: isVerifyingUpdates ? 0.7 : 1
                                            }}
                                        >
                                            {isVerifyingUpdates && editingPostId === carouselEdit ? (
                                                <>
                                                    <div className="spinner_4" style={{ margin: 0, width: '16px', height: '16px' }} />
                                                    Aguarde...
                                                </>
                                            ) : (
                                                <>
                                                    <MdEdit size={18} />
                                                    {editingPostId === carouselEdit ? 'Atualizar' : 'Editar'}
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={handleGeneratePreview}
                                            disabled={isGeneratingPreview}
                                            style={{
                                                backgroundColor: '#1a202c',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: '10px 20px',
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                cursor: isGeneratingPreview ? 'not-allowed' : 'pointer',
                                                opacity: isGeneratingPreview ? 0.7 : 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            {isGeneratingPreview ? (
                                                <>
                                                    <div className="spinner_4" style={{ margin: 0, width: '16px', height: '16px' }} />
                                                    Gerando...
                                                </>
                                            ) : (
                                                <>
                                                    <MdRemoveRedEye size={16} />
                                                    Gerar Novo Preview
                                                </>
                                            )}
                                        </button>
                                        <button
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                background: carouselEdit ? '#ede9fe' : '#f7f7f7',
                                                color: carouselEdit ? '#8E59FF' : '#cccccc',
                                                borderRadius: '8px',
                                                fontWeight: 500,
                                                fontSize: '16px',
                                                border: 'none',
                                                boxShadow: 'none',
                                                padding: '10px 20px',
                                                cursor: carouselEdit ? 'pointer' : 'not-allowed',
                                            }}
                                            onClick={() => carouselEdit && carrosselDownload(carouselEdit)}
                                            disabled={!carouselEdit}
                                        >
                                            <MdDownload size={18} />
                                            Baixar
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Componente do popup de edição de imagens
    const renderImageEditor = () => {
        const handleSaveImage = async () => {
            try {
                // Usar o valor mais recente do selectedUploadImage
                const currentImageUrl = selectedAIImage || selectedUploadImage  //|| selectedAIImage;
                console.log('URL da imagem a ser salva:', currentImageUrl); // Debug
                console.log('selectedUploadImage:', selectedUploadImage); // Debug
                console.log('selectedAIImage:', selectedAIImage); // Debug

                if (currentImageUrl) {
                    // Atualizar a imagem do slide no editedPost
                    if (editedPost && currentSlideId) {
                        const updatedTweets = [...editedPost.tweets];
                        updatedTweets[currentSlideId - 1] = {
                            ...updatedTweets[currentSlideId - 1],
                            urlImagem: currentImageUrl
                        };
                        setEditedPost({
                            ...editedPost,
                            tweets: updatedTweets
                        });
                    }
                }
                setShowImageEditor(false);
            } catch (error) {
                console.error('Erro ao processar imagem:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Erro',
                    text: 'Erro ao processar a imagem',
                    customClass: {
                        popup: 'my-swal-special',
                        title: 'myCustomTitle'
                    }
                });
            }
        };

        return (
            <ChakraProvider>
                <Modal isOpen={showImageEditor} onClose={() => setShowImageEditor(false)} size="xl" motionPreset="scale">
                    <ModalOverlay />
                    <ModalContent sx={{ minHeight: '70vh', height: 'auto', maxWidth: '600px', width: '100%' }}>
                        <ModalHeader>Editar Imagem do Slide</ModalHeader>
                        <ModalCloseButton
                            icon={<span style={{ fontSize: 28, color: '#222', fontWeight: 900, lineHeight: 1 }}>&times;</span>}
                            sx={{
                                background: 'none',
                                border: 'none',
                                boxShadow: 'none',
                                color: '#222',
                                top: '5px',
                                right: '5px',
                                width: '36px',
                                height: '36px',
                                minWidth: '36px',
                                minHeight: '36px',
                                padding: 0,
                                zIndex: 10,
                                _hover: { background: 'none', color: '#000' },
                                _active: { background: 'none', color: '#000' },
                                _focus: { boxShadow: 'none', background: 'none' }
                            }}
                        />
                        <ModalBody>
                            <div className="divAreaImage" style={{ marginTop: '-15px', paddingTop: 0 }}>
                                <div className='imagesContainer' style={{ minHeight: '320px', height: '52vh', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>


                                    {imageSourceType === 'ai' && (
                                        <div className="IAContainer" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
                                            {/* Espaço reservado para imagem ou loading */}
                                            <div style={{
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                minHeight: '36vh',
                                                maxHeight: '36vh',
                                                margin: '0 auto 0 auto',
                                            }}>
                                                {isLoading ? (
                                                    <div className="spinner_4" style={{ margin: 0 }} />
                                                ) : (
                                                    selectedAIImage && (
                                                        <img
                                                            className='imagePreview'
                                                            alt="Preview"
                                                            src={selectedAIImage}
                                                            style={{
                                                                display: 'block',
                                                                maxWidth: '54%',
                                                                maxHeight: '36vh',
                                                                margin: '0 auto 0 auto',
                                                                borderRadius: '16px',
                                                                boxShadow: '0 2px 16px 0 rgba(60,60,60,0.07)'
                                                            }}
                                                        />
                                                    )
                                                )}
                                            </div>
                                            <div className="aiControls" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                                                <textarea
                                                    placeholder="Descreva a imagem que você quer gerar..."
                                                    className="promptInput"
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    value={searchTerm}
                                                    style={{
                                                        width: '96%',
                                                        minHeight: '90px',
                                                        marginBottom: '10px',
                                                        resize: 'vertical',
                                                        paddingTop: '5px',
                                                        paddingLeft: '18px',
                                                        paddingRight: '48px',
                                                        border: '2px solid #8E59FF',
                                                        borderRadius: '10px',
                                                        fontSize: '1.15rem',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                                <button
                                                    className="generateButtonFloating"
                                                    onClick={() => handleIaSubmit({ prompt: searchTerm })}
                                                    disabled={isLoading || !searchTerm}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '4%',
                                                        bottom: '28px',
                                                        width: '44px',
                                                        height: '44px',
                                                        borderRadius: '50%',
                                                        background: '#8E59FF',
                                                        color: 'white',
                                                        border: 'none',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '24px',
                                                        cursor: isLoading || !searchTerm ? 'not-allowed' : 'pointer',
                                                        zIndex: 2,
                                                        transition: 'background 0.2s',
                                                        opacity: isLoading || !searchTerm ? 0.6 : 1
                                                    }}
                                                    title={searchTerm ? "Gerar Imagem" : "Escrever"}
                                                >
                                                    {searchTerm.trim().length === 0 ? <MdEdit /> : <MdSend />}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {imageSourceType === 'own' && (
                                        <div className="uploadContainer" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
                                            {/* Espaço reservado para imagem ou loading */}
                                            <div style={{
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                minHeight: '36vh',
                                                maxHeight: '36vh',
                                                margin: '0 auto 0 auto',
                                            }}>
                                                {isLoading ? (
                                                    <div className="spinner_4" style={{ margin: 0 }} />
                                                ) : selectedUploadImage && (
                                                    <img
                                                        className='imagePreviewOwn'
                                                        alt="Preview"
                                                        src={selectedUploadImage}
                                                        style={{
                                                            display: 'block',
                                                            maxWidth: '54%',
                                                            maxHeight: '36vh',
                                                            margin: '0 auto 0 auto',
                                                            borderRadius: '16px',
                                                            boxShadow: '0 2px 16px 0 rgba(60,60,60,0.07)'
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <div className="fileInputContainer" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'none', border: 'none', boxShadow: 'none', padding: 0, margin: 0 }}>
                                                <input
                                                    type="file"
                                                    id="customFileInput"
                                                    onChange={(e) => uploadImage(e.target.files[0])}
                                                    className="fileInput"
                                                    hidden
                                                    accept="image/*"
                                                />
                                                <label
                                                    htmlFor="customFileInput"
                                                    className="customFileButton"
                                                    style={{
                                                        marginTop: '30px',
                                                        width: 'auto',
                                                        textAlign: 'center',
                                                        fontSize: '1.15rem',
                                                        background: '#ede9fe',
                                                        color: '#8E59FF',
                                                        borderRadius: '14px',
                                                        fontWeight: 400,
                                                        padding: '22px 32px',
                                                        border: 'none',
                                                        boxShadow: 'none',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <MdFileUpload className="buttonIcon" style={{ color: '#8E59FF', fontSize: '1.3em' }} />
                                                    Escolher Arquivo
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="radio-group-footer" style={{ width: '66%', marginLeft: '38px', display: 'flex', justifyContent: 'flex-start' }}>
                                    <ChakraProvider>
                                        <RadioGroup
                                            onChange={handleImageSourceTypeChange}
                                            value={imageSourceType}
                                        >
                                            <Stack direction='row' spacing={{ base: 3, md: 8 }}>
                                                <Radio value="ai" colorScheme="purple">
                                                    Imagens IA
                                                </Radio>
                                                <Radio value="own" colorScheme="purple">
                                                    Minhas Imagens
                                                </Radio>
                                            </Stack>
                                        </RadioGroup>
                                    </ChakraProvider>
                                </div>
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                colorScheme="black"
                                bg="#222"
                                color="#fff"
                                mr={3}
                                onClick={handleSaveImage}
                            >
                                Salvar
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </ChakraProvider>
        );
    };

    // Função handleColorSelection removida - agora gerenciada na tela exclusiva de configurações

    // Funções separadas para o editor de posts
    const handlePostColorSelection = (colors, comboId) => {
        const newColors = {
            Titulos: colors[0],
            Textos: colors[1],
            Fundo: colors[2]
        };

        setPostStyleConfig(prev => ({
            ...prev,
            colors: newColors,
            comboId: comboId,
            useCustomStyle: false
        }));
        setPostPreviewColors(newColors);
    };

    // Função openColorPicker removida - agora gerenciada na tela exclusiva de configurações

    // Função handleColorChange removida - agora gerenciada na tela exclusiva de configurações

    // Função separada para o editor de posts
    const handlePostColorChange = (color) => {
        const newColors = {
            ...postPreviewColors,
            [selectedColorType]: color.hex
        };

        setPostPreviewColors(newColors);
        setPostStyleConfig(prev => ({
            ...prev,
            colors: newColors,
            useCustomStyle: true
        }));
    };

    // Função handleFontChange removida - agora gerenciada na tela exclusiva de configurações

    // Função separada para o editor de posts
    const handlePostFontChange = async (type, value) => {
        if (!value) return;

        // Verificar se a fonte está disponível
        const isAvailable = document.fonts.check(`12px "${value}"`);

        if (!isAvailable) {
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css2?family=${value.replace(/ /g, '+')}:wght@400;700&display=swap`;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        const newFonts = {
            ...postFonts,
            [type]: value
        };

        setPostFonts(newFonts);
        setPostStyleConfig(prev => ({
            ...prev,
            fonts: newFonts,
            useCustomStyle: true
        }));
    };

    // Função handleSaveDefault removida - agora gerenciada na tela exclusiva de configurações

    // Componente de template slide
    const TemplateSlide = ({ template, currentSlide, onNext, onPrev, onSelect, isSelected }) => {
        const boxClassName = `templateBox ${isSelected ? 'selected' : ''}`.trim();

        return (
            <div
                className={boxClassName}
                onClick={(e) => {
                    e.stopPropagation();
                    onSelect(template.title);
                }}
                style={{ cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}
            >
                <div className="templateContainer" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
                    <div className="slideContainer" style={{ width: '100%', height: '220px', borderRadius: '16px', overflow: 'hidden', marginBottom: '8px' }}>
                        <img
                            src={template.slides[currentSlide]?.thumbnailUrl}
                            alt={`Template ${template.title}`}
                            className="templateImage"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '16px',
                                display: 'block',
                                margin: 0
                            }}
                        />
                        <button
                            className="navButton prevButton"
                            onClick={(e) => {
                                e.stopPropagation();
                                onPrev(template.id);
                            }}
                            style={{
                                position: 'absolute',
                                left: '5px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '30px',
                                height: '30px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2
                            }}
                        >
                            <span>‹</span>
                        </button>
                        <button
                            className="navButton nextButton"
                            onClick={(e) => {
                                e.stopPropagation();
                                onNext(template.id);
                            }}
                            style={{
                                position: 'absolute',
                                right: '5px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '30px',
                                height: '30px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2
                            }}
                        >
                            <span>›</span>
                        </button>
                    </div>
                </div>
                <div className="templateName" style={{
                    textAlign: 'center',
                    marginTop: '4px',
                    fontSize: '17px',
                    color: '#4a5568',
                    fontWeight: '500',
                    lineHeight: '1.2',
                    marginBottom: '0',
                    minHeight: 'unset',
                    padding: 0
                }}>
                    {template.name}
                </div>
            </div>
        );
    };

    // Componente de grid de templates
    const TemplateGrid = () => {
        if (loadingTemplates) {
            return (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    Carregando templates...
                </div>
            );
        }

        if (templates.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    Nenhum template disponível
                </div>
            );
        }

        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '28px',
                marginTop: '15px',
                padding: '8px 0'
            }}>
                {templates.map(template => {
                    const isSelected = postSelectedTemplate === template.title;
                    const currentSlide = postCurrentTemplateSlides[template.id] || 0;

                    return (
                        <div key={template.id} style={{
                            background: '#fff',
                            border: isSelected ? '2.5px solid #8E59FF' : '1.5px solid #e2e8f0',
                            borderRadius: '20px',
                            boxShadow: '0 4px 18px 0 rgba(60,60,60,0.07)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            minHeight: '320px',
                            maxWidth: '270px',
                            margin: '0 auto',
                            cursor: 'pointer',
                            transition: 'border 0.2s, box-shadow 0.2s',
                            padding: 0
                        }}
                            onClick={() => handlePostTemplateSelect(template.title)}
                        >
                            <div style={{ width: '100%', aspectRatio: '1/1', position: 'relative', background: '#f7f7f7', overflow: 'visible', zIndex: 10 }}>
                                <img
                                    src={template.slides[currentSlide]?.thumbnailUrl}
                                    alt={`Template ${template.title}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                        borderRadius: 0
                                    }}
                                />
                                {/* Indicador de slides (bolinhas) */}
                                <div style={{
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: '12px',
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    gap: '5px',
                                    zIndex: 3
                                }}>
                                    {template.slides.map((slide, idx) => (
                                        <span
                                            key={idx}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setPostCurrentTemplateSlides(prev => ({ ...prev, [template.id]: idx }));
                                            }}
                                            style={{
                                                width: '7px',
                                                height: '7px',
                                                borderRadius: '50%',
                                                background: idx === currentSlide ? '#8E59FF' : '#fff',
                                                border: '1.2px solid #8E59FF',
                                                display: 'inline-block',
                                                transition: 'background 0.2s',
                                                boxShadow: idx === currentSlide ? '0 0 2px #8E59FF' : 'none',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div style={{
                                width: '100%',
                                padding: '28px 0 18px 0',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                flex: 1
                            }}>
                                <span style={{
                                    fontSize: '14px',
                                    color: '#757575',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    margin: 0,
                                    lineHeight: 1.2
                                }}>{template.name}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Função para buscar templates quando necessário no editor de posts
    const fetchTemplatesForEditor = async () => {
        try {
            setLoadingTemplates(true);
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/templates`);

            const sortedTemplates = response.data.sort((a, b) => {
                if (a.name === "PostSucesso") return -1;
                if (b.name === "PostSucesso") return 1;
                return 0;
            });

            setTemplates(sortedTemplates);

            // Inicializa o estado dos slides 
            const initialSlides = {};
            sortedTemplates.forEach(template => {
                initialSlides[template.id] = 0;
            });
            setPostCurrentTemplateSlides(initialSlides);
        } catch (error) {
            console.error('Erro ao buscar templates:', error);
        } finally {
            setLoadingTemplates(false);
        }
    };

    // Função separada para o editor de posts
    const handlePostTemplateSelect = (templateName) => {
        setPostSelectedTemplate(templateName);
    };

    // Funções nextTemplateSlide e prevTemplateSlide removidas - agora gerenciadas na tela exclusiva de configurações

    // Carregar templates quando o editor de posts for aberto
    useEffect(() => {
        if (isEditMode && templates.length === 0) {
            fetchTemplatesForEditor();
        }
    }, [isEditMode]);

    // Componente ColorPalette removido - agora gerenciado na tela exclusiva de configurações

    // Componente PreviewBox removido - agora gerenciado na tela exclusiva de configurações

    // Renderizar a sidebar fixa
    const renderSidebar = () => {
        return (
            <div className={`sidebar-fixed ${isMobile && isSidebarOpen ? 'active' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-header">
                    <div className="sidebar-logo">
                        <FaRobot size={22} />
                        <span className="menu-text">GrowthPilot</span>
                    </div>
                    <div className="sidebar-toggle-btn" onClick={handleSidebarToggle}>
                        <MdKeyboardArrowLeft size={24} />
                    </div>
                </div>

                <div className="sidebar-menu">
                    <div className="menu-item active">
                        <MdEventAvailable size={20} />
                        <span className="menu-text">Content Planner</span>
                    </div>

                    <div className="menu-item" onClick={() => pciOpen(language)}>
                        <RiCopperCoinLine size={20} />
                        <span className="menu-text">PCI</span>
                    </div>

                    <div className="menu-item" onClick={() => perfilOpen()}>
                        <MdFace size={20} />
                        <span className="menu-text">Avatares</span>
                    </div>

                  {/*  <div className="menu-item" onClick={() => historyOpen(language)}>
                        <MdForum size={20} />
                        <span className="menu-text">Histórico de Conteúdo</span>
                    </div> */}

                    <div className="menu-item" onClick={() => automacaoOpen(language)}>
                        <MdAutorenew size={20} />
                        <span className="menu-text">Automação</span>
                    </div>

                    <div className="menu-item" onClick={() => navigate(`/growthpilot/analytics?lang=${language}`)}>
                        <MdDashboard size={20} />
                        <span className="menu-text">Analytics</span>
                    </div>

                    <div className="menu-item" onClick={() => navigate(`/growthpilot/config?lang=${language}`)}>
                        <MdSettings size={20} />
                        <span className="menu-text">Configurações</span>
                    </div>

                    <div className="menu-item" onClick={() => mainOpen(language)}>
                        <MdArrowBack size={20} />
                        <span className="menu-text">Voltar para o PostSucesso</span>
                    </div>
                </div>

                {/* Botão de suporte na parte inferior */}
                <div className="sidebar-footer">
                    <div className="support-button">
                        <MdLocalActivity size={22} />
                        <span className="menu-text">Suporte</span>
                    </div>
                </div>

            </div>
        );
    };

    // Funções para manipulação de imagens
    const handleImageSourceTypeChange = (sourceType) => {
        setImageSourceType(sourceType);
    };



    const handleNewImagesClick = async () => {
        if (!userSearchTerm) return;

        setIsLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/search-images`, {
                query: userSearchTerm
            });
            setThumbnailImages(response.data);
        } catch (error) {
            console.error('Erro ao buscar imagens:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao buscar imagens',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleUserSearchTermChange = (e) => {
        setUserSearchTerm(e.target.value);
    };

    const uploadImage = async (file) => {
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        try {
            setIsLoading(true);
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/upload-image`, formData);

            if (response.data.url) {
                console.log('Nova URL da imagem:', response.data.url); // Debug
                setSelectedUploadImage(response.data.url);
                setSelectedAIImage('');
                setImageSourceType('own');
                return response.data.url;
            }
        } catch (error) {
            console.error('Erro ao fazer upload da imagem:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao fazer upload da imagem',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleIaSubmit = async (promptData) => {
        try {
            setIsLoading(true);
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/generate-image`, {
                prompt: promptData.prompt,
                style: promptData.style
            });

            if (response.data.url) {
                setSelectedAIImage(response.data.url);
                setSelectedUploadImage('');
                setImageSourceType('ai');
                return response.data.url;
            }
        } catch (error) {
            console.error('Erro ao gerar imagem:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao gerar imagem',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Função para lidar com o clique em uma imagem do slide
    const handleSlideImageClick = (slideId) => {
        setCurrentSlideId(slideId);
        setShowImageEditor(true);
        setImageSourceType('ai'); // Resetar para 'ai' quando abrir o modal
        // Carregar a imagem atual no editor
        const currentSlide = editedPost.tweets[slideId - 1];
        if (currentSlide && currentSlide.urlImagem) {
            setSelectedAIImage(currentSlide.urlImagem);
            setSelectedUploadImage(currentSlide.urlImagem);
        }
    };

    // Função para gerar o preview do carrossel
    const handleGeneratePreview = async () => {
        setIsGeneratingPreview(true);
        try {
            // Resetar plataforma de edição ao gerar novo preview
            setEditingPlatform(null);
            
            // Montar o texto do carrossel no formato esperado
            const carousels = (editedPost.tweets || []).map((tweet, idx) => {
                return `Slide ${idx + 1}:\nHeadline: ${tweet.conteudo || ''}\nTexto: ${tweet.conteudo || ''}`;
            }).join('\n');

            // Montar array de imagens (se já houver)
            const images = (editedPost.tweets || []).map(tweet => tweet.urlImagem || '');
            // Montar array de flip (se houver)
            const flip = (editedPost.tweets || []).map(tweet => tweet.flip || false);
            // Montar array de avgs (se houver)
            const avgs = (editedPost.tweets || []).map(tweet => tweet.avg || '');

            // Buscar perfilPicture
            const perfilPicture = isValidUrl(localStorage.getItem('perfil_picture'))
                ? localStorage.getItem('perfil_picture')
                : `${process.env.REACT_APP_AVATAR_BASE_URL}${userId}${process.env.REACT_APP_AVATAR_PARAMS}`;

            // Buscar nome e sobrenome
            const name = localStorage.getItem('name') || '';
            const lastname = localStorage.getItem('lastname') || '';

            // Buscar id de histórico salvo (se houver)
            const savedUserHistoryId = editedPost.id || editedPost._id || '';

            // Debug: verificar qual ID está sendo enviado
            console.log('GrowthPilot Post ID sendo enviado:', editedPost._id || editedPost.id);
            console.log('EditedPost completo:', editedPost);

            // Chamar o endpoint para gerar os slides
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/generate-slides`, {
                carousels,
                images,
                avgs,
                userId,
                instaId: editedPost.instaId || '',
                savedUserHistoryId,
                totalTime: 0,
                totalToken: 0,
                perfilPicture,
                flip,
                name,
                lastname,
                carouselType: postSelectedTemplate || 'Modelo PostSucesso',
                colors: [
                    postStyleConfig.colors.Titulos,
                    postStyleConfig.colors.Textos,
                    postStyleConfig.colors.Fundo
                ],
                titleFont: postStyleConfig.fonts.titleFont,
                textFont: postStyleConfig.fonts.textFont,
                layoutValue: postStyleConfig.aspectRatio === '4:5' ? '4x5' : 'sqr',
                gpt4: false,
                BackGroundId: editedPost.backgroundId || '',
                isGrowthPilot: true,
                growthPilotPostId: editedPost.id
            });

            // Atualizar as imagens dos slides no estado
            if (response.data && response.data.imageUrls) {
                setEditedPost(prev => ({
                    ...prev,
                    tweets: (prev.tweets || []).map((tweet, idx) => ({
                        ...tweet,
                        slidePreview: response.data.imageUrls[idx] || tweet.slidePreview
                    }))
                }));
            }
            
            // Armazenar o presentationId para download
            if (response.data && response.data.presentationId) {
                setCarouselEdit(response.data.presentationId);
            }
            
            setPreviewGenerated(true);
        } catch (error) {
            console.error('Erro ao gerar preview:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao gerar o preview do carrossel',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
        } finally {
            setIsGeneratingPreview(false);
        }
    };

    // Função para baixar o carrossel
    const carrosselDownload = async (presentationId) => {
                    toast.info(
                t('main', 'imagesPreparing'),
                {
                    position: toast.POSITION.BOTTOM_LEFT,
                    autoClose: 6000,
                    style: {
                        background: 'white',
                    },
                    className: 'growthpilot-toast',
                    icon: 'ℹ',
                    iconStyle: {
                        fontSize: '28px'
                    }
                }
            );

        try {
            // Fazemos a chamada para gerar e baixar o ZIP.
            const downloadResponse = await axios({
                url: process.env.REACT_APP_API_BASE_URL + `/api/generate-zip`,
                method: 'POST',
                responseType: 'blob',
                data: { 
                    presentationId: presentationId,
                    isGrowthPilot: true
                }
            });

            const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${presentationId}.zip`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Erro ao baixar o arquivo ZIP:", error);
        }
    };

    // Função para abrir o Google Slides para edição (modificada para incluir Canva)
    const handleEditClick = async () => {
        if (!carouselEdit) {
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Nenhum carrossel disponível para editar',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
            return;
        }

        try {
            // Atualiza a flag de edição antes de prosseguir com a edição
            const flagResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/editFlag`, {
                presentationId: carouselEdit,
                edit: '1',
                isGrowthPilot: true
            });

            if (flagResponse.status === 200) {
                // Só procede se o flag foi atualizado com sucesso
                // Verificar se já foi escolhida uma plataforma para este post
                if (editingPlatform === 'canva') {
                    // Se já escolheu Canva, abrir diretamente
                    handleCanvasConnectAfterLogin(carouselEdit, editedPost?.canvaDesingEditId);
                } else if (editingPlatform === 'google') {
                    // Se já escolheu Google, abrir diretamente
                    window.open("https://docs.google.com/presentation/d/" + carouselEdit, '_blank');
                } else {
                    // Primeira vez editando, mostrar escolha
                    handleEditChoice(carouselEdit);
                }
                
                // Ativar modo de edição para este post específico
                setEditingPostId(carouselEdit);
            } else {
                // Caso a atualização do flag falhe, lança um erro
                throw new Error(`Falha ao atualizar a flag de edição: ${flagResponse.data?.message || 'Erro desconhecido'}`);
            }
        } catch (error) {
            console.error('Erro ao editar post:', error);
            
            // Exibe uma mensagem de erro para o usuário
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao abrir editor. Tente novamente.',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
        }
    };

    // Função para verificar se há atualizações disponíveis
    const handleVerifyClick = async () => {
        if (!carouselEdit) {
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Nenhum carrossel disponível para verificar',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
            return;
        }

        console.log('Iniciando verificação de atualizações para presentationId:', carouselEdit);
        setIsVerifyingUpdates(true);
        
        try {
            // Tentar atualizar os slides
            await updateSlides();
            
            // Se chegou até aqui, significa que foi bem-sucedido
            console.log('Verificação concluída com sucesso');
            
            // Aguardar 3 segundos para garantir que as imagens foram carregadas e atualizadas
            setTimeout(() => {
                setEditingPostId(null); // Desativar modo de edição para este post
                setIsVerifyingUpdates(false); // Desativar loading do botão
                
                Swal.fire({
                    icon: 'success',
                    title: 'Sucesso',
                    text: 'Slides atualizados com sucesso!',
                    customClass: {
                        popup: 'my-swal-special',
                        title: 'myCustomTitle'
                    }
                });
            }, 3000);
            
        } catch (error) {
            console.error('Erro ao verificar slides:', error);
            
            // Se não conseguiu atualizar, pedir para tentar novamente
            Swal.fire({
                icon: 'info',
                title: 'Aguarde um momento',
                text: 'A atualização ainda não está disponível. Aguarde alguns segundos e tente novamente.',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                },
                confirmButtonText: 'Entendi'
            });
        } finally {
            setIsVerifyingUpdates(false);
        }
    };

    // Função para validar token do Canva
    async function validateCanvaToken(userId) {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/validate-canva-token`, {
                userId
            });
            return response.data.success; // Retorna true se o token é válido, false se não é válido
        } catch (error) {
            console.error('Erro ao validar token do Canva:', error);
            return false; // Retorna false em caso de erro na requisição
        }
    }

    // Função para conectar com o Canva (reutilizada do CustomDrawer.js)
    const handleCanvasConnect = async () => {
        const code_verifier = CryptoJS.lib.WordArray.random(96).toString(CryptoJS.enc.Base64url);
        const code_challenge = CryptoJS.SHA256(code_verifier).toString(CryptoJS.enc.Base64url);

        const userId = localStorage.getItem('userId');
        const language = localStorage.getItem('language');

        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/users/store-session`, {
            userId: userId,
            codeVerifier: code_verifier
        });

        const sessionId = userId + ':' + language + ':' + process.env.REACT_APP_UAT;
        const redirectUri = `${process.env.REACT_APP_URL_REDIRECT}/logoscreen`;
        const clientId = process.env.REACT_APP_CANVA_CLIENT_ID.replace(/['"]/g, "");
        const scopes = process.env.REACT_APP_CANVA_SCOPES;

        const authorizationUrl = `https://www.canva.com/api/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scopes)}&code_challenge=${code_challenge}&code_challenge_method=S256&state=${encodeURIComponent(sessionId)}`;

        let authWindow;

        if (isMobile) {
            window.location.href = authorizationUrl;
        } else {
            const width = 600;
            const height = 600;
            const left = (window.innerWidth / 2) - (width / 2);
            const top = (window.innerHeight / 2) - (height / 2);
            authWindow = window.open(authorizationUrl, 'CanvaAuth', `width=${width},height=${height},top=${top},left=${left}`);

            // Espera o fechamento da janela e, ao fechar, verifica o token e segue o fluxo
            let n = 0;
            const interval = setInterval(async () => {
                if (authWindow && authWindow.closed) {
                    clearInterval(interval);
                    if (n === 0) {
                        let success = await validateCanvaToken(userId); n++;
                        if (success) {
                            Swal.fire({
                                title: 'Sucesso',
                                text: 'Conexão com Canva realizada com sucesso.',
                                icon: 'success',
                                customClass: {
                                    popup: 'my-swal-special',
                                    title: 'myCustomTitle'
                                },
                                confirmButtonText: 'Ok'
                            }).then(() => {
                                // Após login bem-sucedido, abrir o editor do Canva
                                handleCanvasConnectAfterLogin(carouselEdit, null);
                            });
                        } else {
                            Swal.fire({
                                title: 'Erro na conexão',
                                text: 'Não foi possível conectar com o Canva. Tente novamente.',
                                icon: 'error',
                                customClass: {
                                    popup: 'my-swal-special',
                                    title: 'myCustomTitle'
                                },
                                confirmButtonText: 'Ok'
                            });
                        }
                    }
                }
            }, 1000);
        }
    };

    // Função para conectar com o Canva após login bem-sucedido
    const handleCanvasConnectAfterLogin = async (presentationId, canvaDesingEditId) => {

        try {
            // Se já existe um designId e a plataforma é Canva, usar history: true
            const useHistory = canvaDesingEditId && editingPlatform === 'canva';

            if (!useHistory) {

                toast.info(
                    t('main', 'openingCanva'), {
                    position: toast.POSITION.BOTTOM_LEFT,
                    autoClose: 25000,
                    className: 'growthpilot-toast',
                    icon: 'ℹ',
                    iconStyle: {
                        fontSize: '28px'
                    }
                });
            }

            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/edit-canvas`, {
                userId,
                presentationId,
                history: useHistory,
                isGrowthPilot: true,
                forceNewDesign: !useHistory, // Só forçar novo design se não usar history
                clearExistingDesign: !useHistory // Só limpar se não usar history
            });

            const { success, message, canvasUrl, designId } = response.data;

            if (success) {
                // Atualizar o estado local com o designId
                if (editedPost) {
                    setEditedPost(prev => ({
                        ...prev,
                        canvaDesingEditId: designId
                    }));
                }

                // Aguardar mais um pouco antes de abrir o Canva
                await new Promise(resolve => setTimeout(resolve, 500));

                if (!isMobile) {
                    window.open(canvasUrl, '_blank');
                } else {
                    Swal.fire({
                        title: t('main', 'confirmCanvaConnectionTitle'),
                        text: t('main', 'confirmCanvaConnectionText'),
                        icon: 'question',
                        showCancelButton: true,
                        confirmButtonText: t('main', 'confirmCanvaConnectionConfirmButton'),
                        cancelButtonText: t('main', 'confirmCanvaConnectionCancelButton'),
                        customClass: {
                            popup: 'my-swal-special',
                            title: 'myCustomTitle'
                        }
                    }).then((result) => {
                        if (result.isConfirmed) {
                            window.open(canvasUrl, '_blank');
                        }
                    });
                }
            } else {
                throw new Error(message);
            }
        } catch (error) {
            console.error('Erro ao conectar com o Canva:', error);
            Swal.fire({
                title: t('main', 'canvaConnectionErrorTitle'),
                text: t('main', 'canvaConnectionErrorText'),
                icon: 'error',
                confirmButtonText: 'Ok',
                customClass: {
                    popup: 'my-swal-special',
                    title: 'myCustomTitle'
                }
            });
        }
    };

    // Função para lidar com a escolha de edição (ajustada para sempre gerar nova versão ao escolher Canva)
    const handleEditChoice = async (presentationId) => {
        Swal.fire({
            title: t('main', 'editCarouselTitle'),
            text: t('main', 'editCarouselText'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('main', 'editCarouselConfirmButton'),
            cancelButtonText: t('main', 'editCarouselCancelButton'),
            reverseButtons: true,
            customClass: {
                popup: 'my-swal-special',
                title: 'myCustomTitle',
                confirmButton: 'custom-confirm-button-class'
            },
            onOpen: () => {
                // Aqui adicionamos o ícone ao botão depois que o Swal é aberto
                const confirmButton = document.querySelector('.custom-confirm-button-class');
                if (confirmButton) {
                    confirmButton.innerHTML = `<img src=\"https://www.canva.dev/assets/connect/1.svg\" alt=\"Canva Icon\" style=\"width: 16px; height: 16px; vertical-align: middle; margin-right: 5px;\"> ${confirmButton.textContent}`;
                }
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                // Definir plataforma como Canva
                setEditingPlatform('canva');
                
                // Limpar design anterior antes de abrir Canva
                if (editedPost) {
                    setEditedPost(prev => ({
                        ...prev,
                        canvaDesingEditId: null
                    }));
                }
                
                // Sempre gerar uma nova versão ao escolher Canva
                const isLoggedIn = await validateCanvaToken(userId);
                if (isLoggedIn) {
                    // Sempre gerar nova versão e abrir Canva
                    handleCanvasConnectAfterLogin(presentationId, null); // sempre força nova versão
                } else {
                    handleCanvasConnect();
                }
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                // Definir plataforma como Google
                setEditingPlatform('google');
                
                // Limpar canvaDesingEditId quando escolher Google Slides
                if (editedPost) {
                    setEditedPost(prev => ({
                        ...prev,
                        canvaDesingEditId: null
                    }));
                }
                window.open("https://docs.google.com/presentation/d/" + presentationId, '_blank');
            }
        });
    };

    // Função para atualizar os slides (baseada no MainScreen.js e HistoryScreen.js)
    const updateSlides = async () => {
        try {
            console.log('Chamando updateSlides para presentationId:', carouselEdit);
            
            // Verificar se há um designId do Canva E se o usuário está editando via Canva
            const canvaDesingEditId = editedPost?.canvaDesingEditId;
            const isEditingViaCanva = editingPostId === carouselEdit && canvaDesingEditId;
            
            if (isEditingViaCanva) {
                // Atualiza design via Canva
                console.log('Atualizando via Canva com designId:', canvaDesingEditId);
                
                Swal.fire({
                    icon: 'warning',
                    title: 'Atenção',
                    text: 'Certifique-se de que suas alterações no Canva foram salvas antes de continuar.',
                    confirmButtonText: 'Ok',
                    customClass: {
                        popup: 'my-swal-special',
                        title: 'myCustomTitle',
                    },
                });

                try {
                    const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/export-design`, {
                        userId,
                        designId: canvaDesingEditId,
                        presentationId: carouselEdit,
                    });

                    if (response.data.success) {
                        const updatedImageUrls = response.data.imageUrls;
                        const updatedCoverImage = updatedImageUrls.find(url => url.includes("image01"));
                        const updatedCoverImageWithTimestamp = `${updatedCoverImage}?timestamp=${new Date().getTime()}`;

                        // Atualiza a flag de edição para 0
                        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/editFlag`, {
                            presentationId: carouselEdit,
                            edit: '0',
                            isGrowthPilot: true
                        });

                        // Atualizar as imagens dos slides no estado
                        if (updatedImageUrls && updatedImageUrls.length > 0) {
                            setEditedPost(prev => ({
                                ...prev,
                                tweets: (prev.tweets || []).map((tweet, idx) => ({
                                    ...tweet,
                                    slidePreview: updatedImageUrls[idx] ? `${updatedImageUrls[idx]}?t=${Date.now()}` : tweet.slidePreview
                                }))
                            }));
                        }

                        console.log('Slides atualizados via Canva com sucesso');
                    } else {
                        throw new Error(response.data.message || 'Erro ao exportar o design do Canva.');
                    }
                } catch (error) {
                    console.error('Erro ao importar design do Canva:', error);
                    throw new Error('Erro ao atualizar slides via Canva');
                }
            } else {
                // Atualiza slides via Google Presentation
                console.log('Atualizando via Google Slides');
                
                const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/updateSlides`, {
                    userId: userId,
                    presentationId: carouselEdit,
                    isGrowthPilot: true
                });

                console.log('Resposta do updateSlides:', response.data);

                // Verificar se foi bem-sucedido
                if (response.data && response.data.success) {
                    console.log('UpdateSlides bem-sucedido!');
                    
                    // Usar imageUrls diretamente do response (mais rápido)
                    if (response.data.imageUrls && response.data.imageUrls.length > 0) {
                        console.log('Usando imageUrls diretamente do response:', response.data.imageUrls);
                        
                        setEditedPost(prev => ({
                            ...prev,
                            tweets: (prev.tweets || []).map((tweet, idx) => ({
                                ...tweet,
                                slidePreview: response.data.imageUrls[idx] ? `${response.data.imageUrls[idx]}?t=${Date.now()}` : tweet.slidePreview
                            }))
                        }));
                        
                        console.log('Imagens atualizadas imediatamente com URLs do servidor');
                    } else {
                        console.log('Nenhuma imageUrl retornada, buscando do banco...');
                        
                        // Fallback: buscar do banco se não tiver imageUrls diretas
                        try {
                            const postResponse = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/calendar/get-post`, {
                                userId: localStorage.getItem('userId'),
                                totenId: localStorage.getItem('totenId'),
                                calendarPostId: editedPost.id,
                                calendarId: editedPost.calendarId
                            });

                            if (postResponse.data.success && postResponse.data.post) {
                                console.log('Dados do post atualizados do banco:', postResponse.data.post);
                                
                                setEditedPost(prev => ({
                                    ...prev,
                                    ...postResponse.data.post,
                                    tweets: postResponse.data.post.tweets?.map((tweet, index) => ({
                                        ...tweet,
                                        slidePreview: tweet.slidePreview ? `${tweet.slidePreview}?t=${Date.now()}` : (prev.tweets?.[index]?.slidePreview ? `${prev.tweets[index].slidePreview}?t=${Date.now()}` : null)
                                    })) || prev.tweets?.map(tweet => ({
                                        ...tweet,
                                        slidePreview: tweet.slidePreview ? `${tweet.slidePreview}?t=${Date.now()}` : tweet.slidePreview
                                    }))
                                }));
                            }
                        } catch (reloadError) {
                            console.error('Erro ao recarregar dados do post:', reloadError);
                        }
                    }
                } else {
                    throw new Error('Falha ao atualizar slides');
                }
            }
        } catch (error) {
            console.error('Erro ao atualizar slides:', error);
            throw error; // Re-throw para ser capturado pela função chamadora
        }
    };

    
    // Carregar todas as fontes do Google Fonts no início
    useEffect(() => {
        const loadFonts = async () => {
            try {
                const validFonts = fontList.filter(font => font.value);
                const fontFamilies = validFonts
                    .map(font => `family=${font.value.replace(/ /g, '+')}:wght@400;700`)
                    .join('&');

                const url = `https://fonts.googleapis.com/css2?${fontFamilies}&display=swap`;

                const existingLink = document.querySelector(`link[href^="https://fonts.googleapis.com/css2"]`);
                if (existingLink) {
                    existingLink.href = url;
                } else {
                    const link = document.createElement('link');
                    link.href = url;
                    link.rel = 'stylesheet';
                    document.head.appendChild(link);
                }

                await document.fonts.ready;

                validFonts.forEach(font => {
                    const isLoaded = document.fonts.check(`12px "${font.value}"`);
                });

            } catch (error) {
                console.error('Erro ao carregar fontes:', error);
            }
        };

        loadFonts();
    }, [fontList]);

    return (
        <div className="growth-pilot">
            {/* Sidebar fixa no lado esquerdo */}
            {renderSidebar()}

            {/* Conteúdo principal */}
            <div className={`main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${isEditMode ? 'no-scroll' : ''}`}>
                {/* Topo: Título e Foto do Usuário */}
                <div className="growthpilot-topbar">
                    {isMobile && (
                        <div className="mobile-menu-toggle" onClick={handleHamburguerClick}>
                            <MdMenu size={28} />
                        </div>
                    )}
                    <div className="growthpilot-title-container">
                        <div className="growthpilot-title">
                            {isEditMode ? <MdEdit size={24} className="title-icon" /> : <MdDashboard size={24} className="title-icon" />}
                            {isEditMode ? 'Editando Post do Carrossel' : 'GrowthPilot'}
                        </div>
                    </div>
                    <div className="topbar-actions">
                        <div className="growthpilot-user growthpilot-user-large">
                            <UserProfileImage userId={userId} perfilPicture={localStorage.getItem('perfil_picture')} />
                        </div>
                    </div>
                </div>

                {/* Container principal com alinhamento consistente */}
                <div className="growthpilot-grid-container">
                    {/* Header do Content Planner - só mostra quando NÃO estiver no modo de edição */}
                    {!isEditMode && (
                        <div className="content-planner-header">
                            <div className="content-planner-info">
                                <div className="content-planner-title">Content Planner</div>
                                <div className="content-planner-desc">
                                    Seu conteúdo é criado e gerenciado por agentes de IA, que analisam tendências e resultados <br /> para turbinar sua presença digital,
                                    sem esforço manual.
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Área destinada ao calendário ou editor */}
                    <div className={`growthpilot-calendar-area ${isEditMode ? 'edit-mode' : ''}`}>
                        {isEditMode && editingPost && editedPost ? (
                            /* Renderizar o editor de post */
                            renderPostEditor()
                        ) : isLoading ? (
                            <div className="empty-calendar-container">
                                <div className="loading-spinner"></div>
                            </div>
                        ) : isCalendarLoaded ? (
                            /* Renderizar o calendário se houver dados */
                            <div className="calendar-container">
                                {isNavigating && (
                                    <div className="navigation-loading-overlay">
                                        <div className="navigation-spinner">
                                            <div className="spinner-dot"></div>
                                            <div className="spinner-dot"></div>
                                            <div className="spinner-dot"></div>
                                        </div>
                                    </div>
                                )}
                                {showCacheIndicator && (
                                    <div className={`cache-indicator ${showCacheIndicator ? 'show' : ''}`}>
                                        ⚡ Cache
                                    </div>
                                )}
                                {calendarRender}
                            </div>
                        ) : (
                            /* Mostrar status e botão se não houver dados do calendário */
                            <div className="empty-calendar-container">
                                {/* Status flags - Mostrar apenas se não houver dados do calendário */}
                                <div className="growthpilot-status-flags">
                                    <div className={`status-flag ${isPciActive ? 'status-active' : 'status-inactive'}`}>
                                        <RiCopperCoinLine />
                                        <span>{isPciActive ? 'PCI Ativo' : 'PCI Inativo'}</span>
                                        {isPciActive ? (
                                            <FaCheckCircle className="status-icon" style={{ color: '#222' }} />
                                        ) : (
                                            <FaRegTimesCircle className="status-icon" style={{ color: '#222' }} />
                                        )}
                                    </div>
                                    <div className={`status-flag ${isInstagramConnected ? 'status-active' : 'status-inactive'}`}>
                                        <FaInstagram />
                                        <span>{isInstagramConnected ? 'Instagram Conectado' : 'Instagram Desconectado'}</span>
                                        {isInstagramConnected ? (
                                            <FaCheckCircle className="status-icon" style={{ color: '#222' }} />
                                        ) : (
                                            <FaRegTimesCircle className="status-icon" style={{ color: '#222' }} />
                                        )}
                                    </div>
                                </div>

                                {/* Botão de carregar calendário (habilitado se ambos os status estiverem ativos) */}
                                <button
                                    className="btn-load-calendar"
                                    disabled={!(isPciActive && isInstagramConnected)}
                                    onClick={handleTestAgent}
                                >
                                    Carregar Calendário
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Botão de assistente de IA com tooltip/balão */}
                <div className="ai-assistant-container">
                    <button
                        className="ai-assistant-button"
                        onClick={isEditMode ? () => handleSavePost(editedPost) : undefined}
                    >
                        {isEditMode ? <MdSave /> : <FaRobot />}
                    </button>
                    {!isEditMode && (
                        <div className="ai-assistant-tooltip">
                            <div className="tooltip-arrow"></div>
                            <p>
                                Nossos agentes inteligentes analisam continuamente o desempenho dos seus conteúdos e ajustam automaticamente seu calendário para alcançar os melhores resultados.<br />
                                <strong>Próxima atualização agendada:</strong>
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Popup de edição de imagens */}
            {renderImageEditor()}

            {/* Modal do Color Picker global, para funcionar em todas as abas */}
            {showColorPicker && (
                <ChakraProvider>
                    <Modal isOpen={showColorPicker} onClose={() => setShowColorPicker(false)}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalBody display="flex" justifyContent="center">
                                <SketchPicker
                                    color={postPreviewColors[selectedColorType] || '#000'}
                                    onChange={handlePostColorChange}
                                />
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    onClick={() => setShowColorPicker(false)}
                                    sx={{
                                        backgroundColor: '#1a202c',
                                        color: 'white',
                                    }}
                                >
                                    Confirmar
                                </Button>
                            </ModalFooter>
                        </ModalContent>
                    </Modal>
                </ChakraProvider>
            )}

            {/* Modal de configurações removido - agora gerenciado na tela exclusiva de configurações */}

            {/* Botão flutuante de voltar, exatamente igual ao botão do robozinho em tamanho, posição e comportamento visual */}
            {isEditMode && (
                <button
                    className="ai-assistant-button"
                    onClick={handleBackToCalendar}
                    style={{
                        position: 'fixed',
                        bottom: '115px',
                        right: '38px',
                        
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        zIndex: 1100
                    }}
                >
                    <MdArrowBack size={28} />
                </button>
            )}
        </div>
    );
}

export default GrowthPilot;